<script setup>
import {h, nextTick, onMounted, reactive, render, toRaw, ref, computed, watch} from "vue";
import mapboxgl from 'mapbox-gl';
import MapboxDirections from '@mapbox/mapbox-gl-directions/dist/mapbox-gl-directions';
import '@mapbox/mapbox-gl-directions/dist/mapbox-gl-directions.css';
import checked from './checked.png';
import claim from './claim.png';
import claimFire from './claimFire.png';
import claimReopened from './claimReopened.png';
import fcuser from './fcuser.png';
import prefuser from './prefuser.png';
import user from './user.png';
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {activeComponentStore} from "@/Stores/ActiveComponentStore";
import {ToastUtil} from "@/Utils/ToastUtil";
import {RouterLink} from "vue-router";
import {AxiosError} from "axios";

const compName = 'BulkAssignComponent';

const localPath = import.meta.env.VUE_APP_REDIRECT_URL;

const MAP_COLUMNS_SIZE = 12;
const MAP_CLAIMS_COLUMNS_SIZE = 12;
const MAP_PERSONS_COLUMNS_SIZE = 8;

//smaller number is closer, see docs.
const MAP_DEFAULT_ZOOM_LEVEL = 4;
const MAP_SEARCH_ZOOM_LEVEL = 9;
const MAP_CLUSTER_MAX_ZOOM = 5;

const MAP_CLUSTER_DEFAULT_RADIUS = 15;

const MAP_DEFAULT_CENTER_COORDS = [-99.121313, 39.816975];

const props = defineProps({
    contextIdA: String
});

onMounted(() => {

    //TODO: transfer this access token to AWS Secrets Manager
    mapboxgl.accessToken =
        'pk.eyJ1IjoiZW50cmVvcyIsImEiOiJja2RydG16MXEwZ3hvMnhuOXVlcHZhcWRvIn0.mlZA3MAQSFupaQbmFjA9QQ'

    init('streets-v11');
});

const data = reactive({
    directions: {blank: 'blank'},
    claimAge: null,
    origin: null,
    drawer: false,
    streetView: true,
    sortedFirstCall: [],
    sortedPreferred: [],
    showingDirections: false,
    bulkData: {
        type: 'FeatureCollection',
        crs: {
            type: 'name',
            properties: {name: 'urn:ogc:def:crs:OGC:1.3:CRS84'},
        },
        features: [],
    },
    clustering: true,
    showingAppointments: false,
    searchAdjuster: '',
    bulkHeaders: [
        {
            title: 'Claim Number',
            key: 'claimnumber',
        },
        {
            title: 'Client Name',
            key: 'clientname',
        },
        {
            title: 'Actions',
            key: 'actions',
        },
    ],
    firstLoad: true,
    popup: '',
    messageDialog: false,
    sendingMessage: false,
    geodata: [],
    map: null,
    sortedPeople: [],
    mapMarkers: [],
    currentChoice: 'claims',
    searchRequest: '',
    dataLoading: true,
    totalClaims: 0,
    mapCols: MAP_COLUMNS_SIZE,
    bulkList: [],
    loading: false,
    search: null,
    filterArr: [],
    options: [],
    person: [],
    claims: [],
    stateModel: [],
    lossMinModel: null,
    lossMaxModel: null,
    stateItems: [
        'AL', 'AK', 'AS', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC',
        'FM', 'FL', 'GA', 'GU', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS',
        'KY', 'LA', 'ME', 'MH', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO',
        'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'MP',
        'OH', 'OK', 'OR', 'PW', 'PA', 'PR', 'RI', 'SC', 'SD', 'TN',
        'TX', 'UT', 'VT', 'VI', 'VA', 'WA', 'WV', 'WI', 'WY'
    ],
    menuItems: [
        {
            title: 'Client',
            items: null,
            filterActive: false,
        },
    ],
    firstCallModel: false,
    preferredModel: false,
    statusModel: true,
    apps: [],
    preferredItems: ['false', 'true'],
    certificationItems: [],
    certificationModel: [],
    certificationLabel: null,

    statusItems: [
        {
            title: 'Active',
        },
        {
            title: 'Inactive',
        },
    ],
    personMenuItems: [
        {
            title: 'Preferred',
        },
        {
            title: 'Status',
        },
        {
            title: 'Certifications',
        },
    ],
    markers: [],
    showMultipleAdjusterConfirmation: false,
    claimsWithAdjusters: [],
    currentSelectedAdjuster: '',
    currentSelectedAdjusterId: '',

    personSelection: []      //selected adjuster ids (Send Text mode)
});

const formatPhoneNumber = (phoneNumberString) => {
    let cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return null;
}

const init = (type) => {
    data.map = new mapboxgl.Map({
        container: 'map',
        style: 'mapbox://styles/mapbox/' + type,
        center: MAP_DEFAULT_CENTER_COORDS,
        zoom: MAP_DEFAULT_ZOOM_LEVEL
    });
    //FIXME We should add max-bounds to limit zoom-out...why do AAN users need to view the EU, or China?
    //E.g. https://stackoverflow.com/questions/35449568/mapbox-gl-zoom-scope-control

    data.map.on('load', function () {
        data.map.loadImage(claim, function (error, image) {
            if (error) throw error;
            data.map.addImage('claim', image);
        });

        data.map.loadImage(claimFire, function (error, image) {
            if (error) throw error;
            data.map.addImage('claimFire', image);
        });

        data.map.loadImage(claimReopened, function (error, image) {
            if (error) throw error;
            data.map.addImage('claimReopened', image);
        });

        data.map.loadImage(fcuser, function (error, image) {
            if (error) throw error;
            data.map.addImage('fc-person', image);
        });

        data.map.loadImage(prefuser, function (error, image) {
            if (error) throw error;
            data.map.addImage('pref-person', image);
        });

        data.map.loadImage(user, function (error, image) {
            if (error) throw error;
            data.map.addImage('non-pref-person', image);
        });

        data.map.loadImage(checked, function (error, image) {
            if (error) throw error;
            data.map.addImage('checked', image);
        });

        if (props.contextIdA !== null && props.contextIdA !== '' && props.contextIdA !== undefined) {
            getDataForClaim(props.contextIdA);

        } else {
            getData();
        }

    });

};

const resetDirections = () => {
    let origin = data.directions.getOrigin();
    data.directions.removeRoutes();
    data.directions.setOrigin(origin.geometry.coordinates);
}
/*
const getCertifications = () => {
  ApiWrapper.post('claims', 'get-certifications')
  .then(response => {
    if(response.certs){
      data.certificationItems = response.certs;
    }
    if(response.ratings){
      data.ratingItems = response.ratings;
    }
  })
  .catch(error => {
    ToastUtil.error(error);
  });
}
*/

const setDirections = (c1, c2) => {
    data.directions.setDestination([c1 * 1, c2 * 1]);
}

const ToggleMapToClaims = () => {
    data.map.removeControl(data.directions);

    if (data.popup) {
        data.popup.remove();
    }

    data.showingDirections = false;
    getData();
}

const AdjustersListingModeSwitch = (msgActive) => {
    data.sendingMessage = msgActive;
    ClearPersonSelectionLists();
}

const ClearPersonSelectionLists = () => {
    data.personSelection = [];
}

const textMessageContents = ref('');     //ref so the textarea's character counter updates while typing
let textMessageRecipients = [];
let textMessageSendToList = [];
let textMessageClaims = [];              //active claims in bulk list at time of Select Recipients btn press
let textMessageFormattedRecipients = '';  //For display only

//Adjusters list: one list with quick filters. data.sortedPeople is everyone, sorted by distance.
const adjusterFilters = reactive({
    search: '',
    preferred: false,     //Preferred and First Call together show people who are either
    firstCall: false,
    textingOnly: false,   //always on while sending texts
    sortBy: 'distance',
    perPage: 15,
    pageNum: 1
});

const ADJUSTER_SORT_OPTIONS = [
    {title: 'Distance', value: 'distance'},
    {title: 'Name', value: 'name'},
    {title: 'Open capacity', value: 'capacity'}
];

const IsTrue = (val) => val === true || val === 't';

//Claims they can still take; null when no limit is set
const GetOpenCapacity = (person) => {
    const limit = Number(person.limit);
    if (person.limit === null || person.limit === 'null' || Number.isNaN(limit)) return null;
    return limit - (Number(person.claimcount) || 0);
}

const IsAtClaimLimit = (person) => {
    const open = GetOpenCapacity(person);
    return open !== null && open <= 0;
}

const filteredAdjusters = computed(() => {
    const search = adjusterFilters.search?.toString().trim().toLowerCase() ?? '';
    const textingOnly = data.sendingMessage === true || adjusterFilters.textingOnly;

    const people = data.sortedPeople.filter((p) => {
        if (search && !p.name?.toLowerCase().includes(search)) return false;
        if (textingOnly && p.texting_enabled !== true) return false;
        if (adjusterFilters.preferred || adjusterFilters.firstCall) {
            const matches = (adjusterFilters.preferred && IsTrue(p.preferred)) || (adjusterFilters.firstCall && IsTrue(p.firstcall));
            if (!matches) return false;
        }
        return true;
    });

    //sortedPeople is already by distance
    if (adjusterFilters.sortBy === 'name') {
        return [...people].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    }
    if (adjusterFilters.sortBy === 'capacity') {
        //Most room first, no limit set last
        return [...people].sort((a, b) => (GetOpenCapacity(b) ?? -Infinity) - (GetOpenCapacity(a) ?? -Infinity));
    }
    return people;
});

//Back to page 1 whenever the list changes shape
watch(() => [adjusterFilters.search, adjusterFilters.preferred, adjusterFilters.firstCall, adjusterFilters.textingOnly, adjusterFilters.sortBy, data.sendingMessage],
    () => {
        adjusterFilters.pageNum = 1;
    });

const FormatAdjusterLocDist = (dist) => {
    return Math.round(dist * 0.621371, 2).toString() + ' Miles';
}

const viewAdjuster = (person) => {
    let e = {lngLat: {}, features: [{properties: {}}]};
    e.features[0].properties = person;

    if (data.popup !== '') {
        data.popup.remove();
    }

    if (data.sendingMessage === true)
        return;

    let stackedPeople = [];
    for (let x in e.features) {
        let newClaim = {
            name: e.features[x].properties.name,
            id: e.features[x].properties.id,
            phone: e.features[x].properties.phone,
            status: e.features[x].properties.status,
            limit: e.features[x].properties.limit,
            claimcount: e.features[x].properties.claimcount
        }

        if (newClaim.status === 'null') {
            newClaim.status = 'N/A';
        }

        if (newClaim.limit === 'null') {
            newClaim.limit = 'Not Set';
        }

        if (newClaim.claimcount === undefined) {
            newClaim.claimcount = 0;
        }

        stackedPeople.push(newClaim);
    }
//FIXME Why are we multiplying a FLOAT64 by 1?! ...this SHOULD be pointless, unless the data is wonky for some dumb reason
    e.lngLat.lng = person.longitude * 1;
    e.lngLat.lat = person.latitude * 1;

    let coordinates = [person.longitude * 1, person.latitude * 1];

    data.map.flyTo({
        center: coordinates,
        zoom: MAP_DEFAULT_ZOOM_LEVEL,
        essential: true, // this animation is considered essential with respect to prefers-reduced-motion
    });
    // Ensure that if thedata.map is zoomed out such that
    // multiple copies of the feature are visible, the
    // popup appears over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    //FIXME The stylizing and markup for this. Current is just, _bad_. Improve
    let htmlString = ''
    for (let y = 0; y < stackedPeople.length; y++) {
        if (stackedPeople.length > 1) {
            htmlString += `<div style="border-bottom: 1px solid black; width: 150px; padding: 5px;">`;
        } else {
            htmlString += `<div style="width: 175px; padding: 5px;">`;
        }
        htmlString += `
      <h1>
          <a style="color:orange; font-weight: 3000;" href="${localPath}userprofile/${stackedPeople[y].id}" target="_blank">
              ${stackedPeople[y].name}
          </a>
      </h1>
      <h2>Mobile #: ${stackedPeople[y].phone} </h2>
      <h2>Claim Count: ${stackedPeople[y].claimcount} </h2>
      <h2>Claim Limit: ${stackedPeople[y].limit} </h2>
      <h2>Status: ${stackedPeople[y].status} </h2>
      <form>
          <label for="aInfo">Additional Info:</label><br>
          <input class="border: 1px solid #ccc;" type="text" id="aInfo" name="aInfo"><br>
      </form>
      <div id="map-popup-assign-claims-btn-${y}" style="width: 100%"></div>
      <div id="map-popup-set-directions-btn-${y}" style="width: 100%"></div>
    </div>`;
    }

    data.popup = new mapboxgl.Popup()
        .setLngLat(coordinates)
        .setHTML(htmlString)
        .addTo(data.map);

    nextTick(() => {
        for (let y = 0; y < stackedPeople.length; y++) {
            const element1 = document.getElementById(`map-popup-assign-claims-btn-${y}`);
            if (element1) {
                render(
                    h(
                        'div',
                        {
                            innerHTML: 'Assign Claims',
                            style: "background-color: white; padding: 2px; border-style: solid; border-color: gray; width: 125px;",
                            onClick: (e) => {
                                assignClaims(
                                    stackedPeople[y].id,
                                    stackedPeople[y].name
                                )
                            }
                        }
                    ),
                    element1);
            }

            const element2 = document.getElementById(`map-popup-set-directions-btn-${y}`);
            if (element2) {
                render(
                    h(
                        'div',
                        {
                            innerHTML: 'Set Directions',
                            style: "background-color: white;padding: 2px; margin-top: 10px; border-style: solid; border-color: gray; width: 125px;",
                            onClick: (e) => {
                                setDirections(
                                    coordinates[0],
                                    coordinates[1]
                                )
                            }
                        }
                    ),
                    element2);
            }
        }

    });
}

const searchMap = () => {

    if (!data.searchRequest || data.searchRequest === '') {
        data.map.easeTo({
            center: MAP_DEFAULT_CENTER_COORDS,
            zoom: MAP_DEFAULT_ZOOM_LEVEL,
            essential: true, // this animation is considered essential with respect to prefers-reduced-motion
        });
        return; //ignore empty value
    }

    let encodedSearch = encodeURI(data.searchRequest.replace('#', ''));
    let map = data.map;
    //FIXME We need to move any kind of API keys to Secrects scope!
    ApiWrapper.getRaw(
        'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
        encodedSearch +
        '.json?access_token=pk.eyJ1IjoiZW50cmVvcyIsImEiOiJja2RydG16MXEwZ3hvMnhuOXVlcHZhcWRvIn0.mlZA3MAQSFupaQbmFjA9QQ'
    )
        .then(function (response) {
            map.flyTo({
                center: response.features[0].center,
                zoom: MAP_SEARCH_ZOOM_LEVEL,
                essential: true, // this animation is considered essential with respect to prefers-reduced-motion
            });
        })
        .catch((err) => {
            ToastUtil.error(err);
        });
}

const getData = (claimid) => {
    data.currentChoice = 'claims';
    data.mapCols = MAP_CLAIMS_COLUMNS_SIZE;
    data.loading = true;

    ApiWrapper.post('claims', 'get-unassigned', {
        client: null
    })
        .then((response) => {
            if (response.data) {
                data.claims = response.data;
            }

            if (response.geodata) {
                data.geodata = response.geodata;
            }

            if (response.app) {
                data.apps = response.app;
            }

            if (claimid) {
                for (let x in data.claims) {
                    if (data.claims[x].claimid === claimid) {
                        data.bulkList.push({
                            claimnumber: data.claims[x].claimnumber,
                            claimid: data.claims[x].claimid,
                            claimtype: data.claims[x].claimtype,
                            clientname: data.claims[x].name,
                            clientid: data.claims[x].clientid,
                            state: data.claims[x].state,
                            long: data.claims[x].longitude,
                            lat: data.claims[x].latitude,
                        })
                    }
                }
            }

            createMapMarkers(response.geodata, 'claim', data.clustering);
        })
        .catch((e) => {
            ToastUtil.error(e);
        })
        .finally(() => {
            data.loading = false
        });
}

const getDataForClaim = (claimid) => {
    data.currentChoice = 'claims';
    data.mapCols = MAP_CLAIMS_COLUMNS_SIZE;
    data.loading = true;
    ApiWrapper.post('claims', 'get-claim-by-id', {
        claimId: claimid
    })
        .then((response) => {
            let claimData = response.data;
            data.claims = response.data;
            data.bulkList.push({
                claimnumber: claimData.claimnumber,
                claimid: claimData.claimid,
                claimtype: claimData.claimtype,
                clientname: claimData.name,
                clientid: claimData.clientid,
                state: claimData.state,
                long: claimData.longitude,
                lat: claimData.latitude,
            });

            data.geodata = response.geodata;
            data.apps = response.app;

            createMapMarkers(response.geodata, 'claim', data.clustering);

            selectPerson();
        })
        .catch((err) => {
            ToastUtil.error(err);
            data.loading = false;
        });
};

const removeFromBulkList = (claim) => {

    for (let x in data.bulkData.features) {
        if (data.bulkData.features[x].properties.id === claim.claimid) {
            data.bulkData.features.splice(x, 1);
        }
    }

    for (let x in data.bulkList) {
        if (data.bulkList[x].claimid === claim.claimid) {
            data.geodata.features.push({
                type: 'Feature',
                properties: {
                    id: data.bulkList[x].claimid,
                    claimnumber: data.bulkList[x].claimnumber,
                    claimtype: data.bulkList[x].claimtype,
                    name: data.bulkList[x].clientname,
                    clientid: data.bulkList[x].clientid,
                    openedStatus: data.bulkList[x].openedStatus
                },
                geometry: {
                    type: 'Point',
                    coordinates: [
                        data.bulkList[x].long,
                        data.bulkList[x].lat,
                        0,
                    ],
                },
            });

            data.bulkList.splice(x, 1);

            if (data.currentChoice === 'claims') {
                createMapMarkers(data.geodata, 'claim', data.clustering)
            }

            break;
        }
    }
}

const removeItemFromBulkList = (claimid) => {

    for (let x in data.bulkList) {
        if (data.bulkList[x].claimid === claimid) {
            data.bulkList.splice(x, 1)
        }
    }

    for (let x in data.bulkData.features) {
        if (data.bulkData.features[x].properties.id === claimid) {

            data.geodata.features.push(data.bulkData.features[x]);

            data.bulkData.features.splice(x, 1);

            createMapMarkers(data.geodata, 'claim', data.clustering)
            break;
        }
    }
}

const addToBulkList = (claim, type, name, clientid, long, lat, id, state, openedStatus) => {
    if (name === 'null') {
        name = 'Not Assigned';
    }

    let addClaim = true
    for (let x in data.bulkList) {
        if (data.bulkList[x].claimid === id) {
            ToastUtil.errorMsg('Claim Already In List');
            addClaim = false;
        }
    }

    if (addClaim) {
        data.bulkList.push({
            claimnumber: claim,
            claimid: id,
            claimtype: type,
            clientname: name,
            clientid: clientid,
            state: state,
            long: long,
            lat: lat,
            openedStatus: openedStatus
        });
    }

    data.popup.remove();

    createMapMarkers(data.geodata, 'claim', data.clustering);
}

const selectPerson = () => {
    if (data.popup) {
        data.popup.remove();
    }

    if (data.bulkList.length === 0) {
        ToastUtil.warningMsg('Please select at least one claim', 3250);

    } else {
        if (data.directions.blank === 'blank') {
            data.showingDirections = true
            data.directions = new MapboxDirections({
                accessToken: mapboxgl.accessToken,
                unit: 'imperial',
                profile: 'mapbox/driving',
                interactive: false,
                flyTo: false,
                controls: {
                    inputs: false,
                    instructions: true,
                    profileSwitcher: false,
                },
            });

            if (data.map.getSource('directions') === undefined) {
                data.map.addControl(data.directions, 'top-left');
            }

        } else {
            if (data.map.getSource('directions') === undefined) {
                data.map.addControl(data.directions, 'top-left');
            }

            data.showingDirections = true;
        }

        let long = 0;
        let lat = 0;
        let length = data.bulkList.length;
        for (let x in data.bulkList) {
            long += parseFloat(data.bulkList[x].long);
            lat += parseFloat(data.bulkList[x].lat);
        }

        long = long / length;
        lat = lat / length;
        data.directions.setOrigin([long, lat]);

        data.map.flyTo({
            center: [long, lat],
            zoom: MAP_DEFAULT_ZOOM_LEVEL,
            essential: true, // this animation is considered essential with respect to prefers-reduced-motion
        });

        data.currentChoice = 'people';
        data.mapCols = MAP_PERSONS_COLUMNS_SIZE;

        data.loading = true;

        ApiWrapper.post('person', 'get-adjusters', {
            status: data.statusModel,
            firstcall: data.firstCallModel,
            preferred: data.preferredModel,
            certification: data.certificationModel,
            licenses: data.stateModel,
            name: data.searchAdjuster,
        })
            .then((response) => {
                data.person = response.data;

                let geodata = {
                    type: 'FeatureCollection',
                    crs: {
                        type: 'name',
                        properties: {name: 'urn:ogc:def:crs:OGC:1.3:CRS84'},
                    },
                    features: [],
                }

                for (let x in response.data) {
                    let newObj = {
                        type: 'Feature',
                        properties: {
                            id: response.data[x].id,
                            name: response.data[x].name,
                            status: response.data[x].status,
                            mobilephone: response.data[x].mobilephone,
                            preferred: response.data[x].preferred,
                            limit: response.data[x].limit,
                            claimcount: response.data[x].claimcount,
                            firstcall: response.data[x].firstcall,
                            texting_enabled: response.data[x].texting_enabled,
                        },
                        geometry: {
                            type: 'Point',
                            coordinates: [response.data[x].longitude, response.data[x].latitude, 0],
                        },
                    };

                    geodata.features.push(newObj);
                }

                let unsorted = [];
                for (let person in response.data) {
                    let curPerson = response.data[person]
                    let distance = getDistanceFromLatLonInKm(lat, long, curPerson.latitude, curPerson.longitude)
                    unsorted.push({
                        title: curPerson.name,
                        distance: distance,
                        name: curPerson.name,
                        firstcall: curPerson.firstcall,
                        id: curPerson.id,
                        phone: curPerson.mobilephone,
                        texting_enabled: curPerson.texting_enabled,
                        preferred: curPerson.preferred,
                        status: curPerson.status,
                        limit: curPerson.limit,
                        claimcount: curPerson.claimcount,
                        latitude: curPerson.latitude,
                        longitude: curPerson.longitude,
                        shortcomment: curPerson.shortcomment
                    })
                }

                unsorted.sort((a, b) => (a.distance > b.distance) ? 1 : -1);

                let firstCallArr = [];
                let preferredArr = [];

                for (let x in unsorted) {
                    if (unsorted[x].firstcall === 't' || unsorted[x].firstcall === true) {
                        firstCallArr.push(unsorted[x]);
                    }
                    if (unsorted[x].preferred === 't' || unsorted[x].preferred === true) {
                        preferredArr.push(unsorted[x]);
                    }
                }

                data.sortedPeople = unsorted;
                data.sortedPreferred = preferredArr;
                data.sortedFirstCall = firstCallArr;

                createMapMarkers(geodata, 'person', data.clustering);
            })
            .catch((err) => {
                ToastUtil.error(err);
            })
            .finally(() => {
                data.loading = false;
            });
    }
}

const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
    let R = 6371; // Radius of the earth in km
    let dLat = deg2rad(lat2 - lat1);  // deg2rad below
    let dLon = deg2rad(lon2 - lon1);
    let a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2)
    ;
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
}

const deg2rad = (deg) => {
    return deg * (Math.PI / 180);
}

const assignClaims = (personId, personName) => {
    if (data.popup) {
        data.popup.remove();
    }

    data.loading = true;
    ApiWrapper.post('person', 'check-assign', {
        personId: personId,
    })
        .then((response) => {
            let claimStates = data.bulkList.map(function (obj) {
                return obj.state.toLowerCase();
            });

            let licenseStates = [];

            if (response.licenseStates.length > 0) {
                licenseStates = response.licenseStates.map(function (obj) {
                    if (obj.state) {
                        if (obj.expired === 'ok') {
                            return obj.state.toLowerCase();
                        }
                    }
                });
            }

            let claimClients = []
            claimClients = data.bulkList.map(function (obj) {
                if (obj.clientid !== undefined) {
                    return obj.clientid.toString().toLowerCase();
                }
            })

            let blockedClients = []

            if (response.clientBlocks.length > 0) {
                blockedClients = response.clientBlocks.map(function (obj) {
                    return obj.clientid.toString().toLowerCase();
                })
            }

            let licensed = 'are';
            for (let x in claimStates) { //("CO", "DC", "IL", "IA", "KS", "MD", "MO", "NE", "NJ", "ND", "OH", "PA", "SD", "TN", "VA", "WI")
                if (claimStates[x] === 'co' || claimStates[x] === 'dc' || claimStates[x] === 'il' || claimStates[x] === 'ks' || claimStates[x] === 'md' || claimStates[x] === 'mo' ||
                    claimStates[x] === 'ne' || claimStates[x] === 'nj' || claimStates[x] === 'nd' || claimStates[x] === 'oh' || claimStates[x] === 'pa' || claimStates[x] === 'sd' || claimStates[x] === 'tn' || claimStates[x] === 'va' || claimStates[x] === 'wi'
                ) {
                    //
                } else {
                    if (licenseStates.indexOf(claimStates[x]) === -1) {
                        licensed = 'are not'
                    }
                }
            }

            let blocked = 'are not';
            for (let x in claimClients) {
                if (blockedClients.indexOf(claimClients[x]) !== -1) {
                    blocked = 'are'
                }
            }

            let claimLimit = '';
            let claimCount = '';

            if (response.currentClaims === undefined) {
                claimCount = 0;
            } else {
                claimCount = response.currentClaims * 1;
            }
            if (response.limit === undefined) {
                claimLimit = 'Not Set';
            } else {
                claimLimit = response.limit;
            }

            if (licensed === 'are not') {
                data.loading = false;
                ToastUtil.errorMsg('This person is not licensed in a required state');

            } else if (claimLimit === 'Not Set' || (claimCount + data.bulkList.length) > claimLimit) {
                data.loading = false;
                ToastUtil.errorMsg('Person will exceed claim limit');

            } else if (blocked === 'are') {
                data.loading = false;
                ToastUtil.errorMsg('This person is blocked from this client');

            } else {
                //check if there is another adjuster assigned before making this assignment
                let claims = toRaw(data.bulkList);
                let claimUuids = [];
                claims.forEach((claim) => {
                    if (claim.claimid) {
                        claimUuids.push(claim.claimid);
                    }
                });

                ApiWrapper.post('claims', 'get-do-claims-have-adjuster', {
                    claimUuids: claimUuids
                })
                    .then((response) => {
                        if (response && response.claimsWithAdjusters) {
                            if (response.claimsWithAdjusters.length > 0) {
                                data.claimsWithAdjusters = response.claimsWithAdjusters
                                //approve multiple adjusters popup
                                data.showMultipleAdjusterConfirmation = true;
                                data.currentSelectedAdjuster = personName;
                                data.currentSelectedAdjusterId = personId;

                            } else {
                                //post the assignment(s)
                                assignBulkClaimsToAdjuster(personId);
                                data.showMultipleAdjusterConfirmation = false;
                            }
                        }
                    })
                    .catch((err) => {
                        ToastUtil.error(err);
                    })
                    .finally(() => {
                        data.loading = false;
                    });
            }
        })
        .catch((err) => {
            ToastUtil.error(err);
        });
}

const assignBulkClaimsToAdjuster = (personId) => {
    ApiWrapper.post('person', 'bulk-assign', {
        claims: data.bulkList,
        personId: personId,
    })
        .then((response) => {
            data.bulkList = []
            data.bulkData = {
                type: 'FeatureCollection',
                crs: {
                    type: 'name',
                    properties: {name: 'urn:ogc:def:crs:OGC:1.3:CRS84'},
                },
                features: [],
            }
            data.map.removeControl(data.directions)
            data.showingDirections = false
            data.directions = {blank: 'blank'}
            activeComponentStore.notifyComponents(['claims'], compName);

            getData();

            ToastUtil.successMsg('Assigned claims');
            //BG ....seriously? This is quite possibly a HUGE amount of pop-up bubbles...
            for (let i = 0; i < response.thirdPartySuccesses.length; i++) {
                ToastUtil.successMsg(response.thirdPartySuccesses[i]);
            }

            for (let i = 0; i < response.xactAddAdjusterError.length; i++) {
                ToastUtil.errorMsg(`Error assigning adjuster in Xact for claim: ${response.xactAddAdjusterError[i]}`);
            }
            for (let i = 0; i < response.thirdPartyAddAdjusterErrors.length; i++) {
                ToastUtil.error(response.thirdPartyAddAdjusterErrors[i]);
            }
        })
        .catch((err) => {
            ToastUtil.error(err);
            data.loading = false;
        })
        .finally(() => {
            data.claimsWithAdjusters = []
            data.currentSelectedAdjuster = '';
            data.currentSelectedAdjusterId = '';
        });
}

const createMapMarkers = (geodata, type, clustering) => {
    // Add a new source from our GeoJSON data and
    // set the 'cluster' option to true. GL-JS will
    // add the point_count property to your source data.
    if (data.firstLoad === false) {
        if (typeof data.map.getLayer('clusters') !== 'undefined') {
            data.map.removeLayer('clusters')
        }
        if (typeof data.map.getLayer('cluster-count') !== 'undefined') {
            data.map.removeLayer('cluster-count')
        }
        if (typeof data.map.getLayer('clustersReopened') !== 'undefined') {
            data.map.removeLayer('clustersReopened')
        }
        if (typeof data.map.getLayer('cluster-countReopened') !== 'undefined') {
            data.map.removeLayer('cluster-countReopened')
        }
        if (typeof data.map.getLayer('fcClusters') !== 'undefined') {
            data.map.removeLayer('fcClusters')
        }
        if (typeof data.map.getLayer('prefClusters') !== 'undefined') {
            data.map.removeLayer('prefClusters')
        }
        if (typeof data.map.getLayer('nonPrefClusters') !== 'undefined') {
            data.map.removeLayer('nonPrefClusters')
        }
        if (typeof data.map.getLayer('fcClusters') !== 'undefined') {
            data.map.removeLayer('fcClusters')
        }
        if (typeof data.map.getLayer('non-pref-unclustered-point') !== 'undefined') {
            data.map.removeLayer('non-pref-unclustered-point')
        }
        if (typeof data.map.getLayer('pref-unclustered-point') !== 'undefined') {
            data.map.removeLayer('pref-unclustered-point')
        }
        if (typeof data.map.getLayer('fc-unclustered-point') !== 'undefined') {
            data.map.removeLayer('fc-unclustered-point')
        }
        if (typeof data.map.getLayer('pref-cluster-count') !== 'undefined') {
            data.map.removeLayer('pref-cluster-count')
        }
        if (typeof data.map.getLayer('non-pref-cluster-count') !== 'undefined') {
            data.map.removeLayer('non-pref-cluster-count')
        }
        if (typeof data.map.getLayer('fc-cluster-count') !== 'undefined') {
            data.map.removeLayer('fc-cluster-count')
        }
        if (typeof data.map.getLayer('unclustered-point') !== 'undefined') {
            data.map.removeLayer('unclustered-point')
        }
        if (typeof data.map.getLayer('unclustered-point-fire') !== 'undefined') {
            data.map.removeLayer('unclustered-point-fire')
        }
        if (typeof data.map.getSource('notype') !== 'undefined') {
            data.map.removeSource('notype')
        }
        if (typeof data.map.getSource('firetype') !== 'undefined') {
            data.map.removeSource('firetype')
        }
        if (typeof data.map.getLayer('unclustered-pointReopened') !== 'undefined') {
            data.map.removeLayer('unclustered-pointReopened')
        }
        if (typeof data.map.getSource('notypeReopened') !== 'undefined') {
            data.map.removeSource('notypeReopened')
        }
        if (typeof data.map.getSource('firstCall') !== 'undefined') {
            data.map.removeSource('firstCall')
        }
        if (typeof data.map.getSource('preferred') !== 'undefined') {
            data.map.removeSource('preferred')
        }
        if (typeof data.map.getSource('nonPreferred') !== 'undefined') {
            data.map.removeSource('nonPreferred')
        }

    } else {

        data.map.addSource('apps', {
            type: 'geojson',
            data: data.apps,
            cluster: false,
        });

        data.map.addLayer({
            id: 'appLayer',
            type: 'symbol',
            source: 'apps',
            paint: {
                'text-halo-blur': 0,
                'text-halo-width': 15,
                'text-halo-color': 'purple',
                'text-color': 'white',
            },
            layout: {
                visibility: 'none',
                'text-allow-overlap': true,
                'text-field': 'A',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 35,
            },
        });

        data.map.on('click', 'appLayer', function (e) {
            if (data.popup !== '') {
                data.popup.remove()
            }
            let stackedApps = []
            let coordinates = e.features[0].geometry.coordinates.slice();

            for (let x in e.features) {
                let newApp = {
                    claimnumber: e.features[x].properties.claimnumber,
                    claimid: e.features[x].properties.claimid,
                    claimtype: e.features[x].properties.claimtype,
                    streetaddress: e.features[x].properties.streetaddress,
                    city: e.features[x].properties.city,
                    state: e.features[x].properties.state,
                    zip: e.features[x].properties.zip,
                    pname: e.features[x].properties.pname,
                    pid: e.features[x].properties.pid,
                    client: e.features[x].properties.client,
                    age: e.features[x].properties.age,
                    claimlimit: e.features[x].properties.claimlimit,
                    curclaims: e.features[x].properties.curclaims,
                    currenttask: e.features[x].properties.currenttask,
                }

                stackedApps.push(newApp);

            }

            let htmlString = ''
            for (let x in stackedApps) {
                htmlString +=
                    `<div style="width: 250px; padding: 5px;">
            <h3>
                <a href="${localPath}claim/${stackedApps[x].claimid}" target="_blank"> Claim: ${stackedApps[x].claimnumber}</a>
            </h3>
            <h3> Client: ${stackedApps[x].client}</h3>
            <h3>
                <a href="${localPath}userprofile/${stackedApps[x].pid}" target="_blank"> Adjuster: ${stackedApps[x].pname}</a>
            </h3>
            <h3> Claim Count: ${stackedApps[x].curclaims} </h3>
            <h3> Claim Limit: ${stackedApps[x].claimlimit} </h3>
            <h3> Current Task: ${stackedApps[x].currenttask} </h3>
        </div>`;
            }

            coordinates = e.features[0].geometry.coordinates.slice();
            // Ensure that if thedata.map is zoomed out such that
            // multiple copies of the feature are visible, the
            // popup appears over the copy being pointed to.
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
            }

            data.popup = new mapboxgl.Popup()
                .setLngLat(coordinates)
                .setHTML(htmlString)
                .addTo(data.map)
        });

        data.firstLoad = false;
    }

    if (data.currentChoice === 'claims') {
        for (let x = 0; x < data.bulkList.length; x++) {
            for (let y = 0; y < geodata.features.length; y++) {
                if (data.bulkList[x].claimid === geodata.features[y].properties.id) {
                    data.bulkData.features.push(geodata.features[y])
                }
            }
        }
    }

    if (data.currentChoice === 'claims') {
        for (let x in geodata.features) {
            for (let y in data.bulkList) {
                if (data.bulkList[y].claimid === geodata.features[x].properties.id) {
                    geodata.features.splice(x, 1)
                }
            }
        }
    }

    if (data.currentChoice === 'people') {
        let firstCallPeople = {
            type: 'FeatureCollection',
            crs: {
                type: 'name',
                properties: {name: 'urn:ogc:def:crs:OGC:1.3:CRS84'},
            },
            features: [],
        }
        let preferredPeople = {
            type: 'FeatureCollection',
            crs: {
                type: 'name',
                properties: {name: 'urn:ogc:def:crs:OGC:1.3:CRS84'},
            },
            features: [],
        }
        let nonPreferredPeople = {
            type: 'FeatureCollection',
            crs: {
                type: 'name',
                properties: {name: 'urn:ogc:def:crs:OGC:1.3:CRS84'},
            },
            features: [],
        }
        for (let x in geodata.features) {
            //split people into three arrays
            if (geodata.features[x].properties.firstcall === true || geodata.features[x].properties.firstcall === 't') {
                firstCallPeople.features.push(geodata.features[x])
            } else if (geodata.features[x].properties.preferred === false || geodata.features[x].properties.preferred === 'f') {
                nonPreferredPeople.features.push(geodata.features[x])
            } else if (geodata.features[x].properties.preferred === true || geodata.features[x].properties.preferred === 't') {
                preferredPeople.features.push(geodata.features[x])
            }
        }
        if (!clustering) {
            //if not clustering make 3 sources without cluster
            data.map.addSource('firstCall', {
                type: 'geojson',
                data: firstCallPeople,
                cluster: false,
            })
            data.map.addSource('preferred', {
                type: 'geojson',
                data: preferredPeople,
                cluster: false,
            })
            data.map.addSource('nonPreferred', {
                type: 'geojson',
                data: nonPreferredPeople,
                cluster: false,
            })
        } else {
            //else make 3 sources with clustering
            data.map.addSource('firstCall', {
                type: 'geojson',
                data: firstCallPeople,
                cluster: true,
                clusterMaxZoom: MAP_CLUSTER_MAX_ZOOM, // Max zoom to cluster points on
                clusterRadius: MAP_CLUSTER_DEFAULT_RADIUS, // Radius of each cluster when clustering points (defaults to 50)
            })
            data.map.addSource('preferred', {
                type: 'geojson',
                data: preferredPeople,
                cluster: true,
                clusterMaxZoom: MAP_CLUSTER_MAX_ZOOM, // Max zoom to cluster points on
                clusterRadius: MAP_CLUSTER_DEFAULT_RADIUS, // Radius of each cluster when clustering points (defaults to 50)
            })
            data.map.addSource('nonPreferred', {
                type: 'geojson',
                data: nonPreferredPeople,
                cluster: true,
                clusterMaxZoom: MAP_CLUSTER_MAX_ZOOM, // Max zoom to cluster points on
                clusterRadius: MAP_CLUSTER_DEFAULT_RADIUS, // Radius of each cluster when clustering points (defaults to 50)
            })
        }
        data.map.addLayer({
            //pref clusters
            id: 'prefClusters',
            type: 'circle',
            source: 'preferred',
            filter: ['has', 'point_count'],
            paint: {
                // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
                // with three steps to implement three types of circles:
                //   * Blue, 20px circles when point count is less than 100
                //   * Yellow, 30px circles when point count is between 100 and 750
                //   * Pink, 40px circles when point count is greater than or equal to 750
                'circle-color': [
                    'step',
                    ['get', 'point_count'],
                    '#FF7051',
                    100,
                    '#FAA142',
                    750,
                    '#FAD942',
                ],
                'circle-radius': [
                    'step',
                    ['get', 'point_count'],
                    20,
                    100,
                    30,
                    750,
                    40,
                ],
            },
        })

        data.map.addLayer({
            //notpref clusters
            id: 'nonPrefClusters',
            type: 'circle',
            source: 'nonPreferred',
            filter: ['has', 'point_count'],
            paint: {
                // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
                // with three steps to implement three types of circles:
                //   * Blue, 20px circles when point count is less than 100
                //   * Yellow, 30px circles when point count is between 100 and 750
                //   * Pink, 40px circles when point count is greater than or equal to 750
                'circle-color': [
                    'step',
                    ['get', 'point_count'],
                    '#FF7051',
                    100,
                    '#FAA142',
                    750,
                    '#FAD942',
                ],
                'circle-radius': [
                    'step',
                    ['get', 'point_count'],
                    20,
                    100,
                    30,
                    750,
                    40,
                ],
            },
        })

        data.map.addLayer({
            //first call clusters
            id: 'fcClusters',
            type: 'circle',
            source: 'firstCall',
            filter: ['has', 'point_count'],
            paint: {
                // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
                // with three steps to implement three types of circles:
                //   * Blue, 20px circles when point count is less than 100
                //   * Yellow, 30px circles when point count is between 100 and 750
                //   * Pink, 40px circles when point count is greater than or equal to 750
                'circle-color': [
                    'step',
                    ['get', 'point_count'],
                    '#FF7051',
                    100,
                    '#FAA142',
                    750,
                    '#FAD942',
                ],
                'circle-radius': [
                    'step',
                    ['get', 'point_count'],
                    20,
                    100,
                    30,
                    750,
                    40,
                ],
            },
        })

        data.map.addLayer({
            //cluster-count pref
            id: 'pref-cluster-count',
            type: 'symbol',
            source: 'preferred',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
            },
        })

        data.map.addLayer({
            //ncluster-count non-pref
            id: 'non-pref-cluster-count',
            type: 'symbol',
            source: 'nonPreferred',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
            },
        })

        data.map.addLayer({
            //fc-cluster-count
            id: 'fc-cluster-count',
            type: 'symbol',
            source: 'firstCall',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': 12,
            },
        });

    } else {
        //current choice claims

        for (let x = geodata.features.length - 1; x >= 0; x--) {
            if (data.claimAge !== null) {
                if (geodata.features[x].properties.age < data.claimAge) {
                    geodata.features.splice(x, 1)
                }
            }
        }

        let geodataReopened = JSON.parse(JSON.stringify(geodata));
        let geodataNew = JSON.parse(JSON.stringify(geodata));
        let geodataFire = JSON.parse(JSON.stringify(geodata));

        for (let x = geodata.features.length - 1; x >= 0; x--) {

            if (geodataReopened.features[x].properties.openedStatus === 'New') {
                geodataReopened.features.splice(x, 1);
            } else if (geodataReopened.features[x].properties.claimtype) {
                if (geodataReopened.features[x].properties.claimtype.toString().toLowerCase().includes('fire'))
                    geodataReopened.features.splice(x, 1);
            }

            if (geodataNew.features[x].properties.openedStatus === 'Reopened') {
                geodataNew.features.splice(x, 1)
            } else if (geodataNew.features[x].properties.claimtype) {
                if (geodataNew.features[x].properties.claimtype.toString().toLowerCase().includes('fire'))
                    geodataNew.features.splice(x, 1);
            }

            if (geodataFire.features[x].properties.claimtype) {
                if (!geodataFire.features[x].properties.claimtype.toString().toLowerCase().includes('fire'))
                    geodataFire.features.splice(x, 1);
            } else {
                geodataFire.features.splice(x, 1);
            }

        }

        if (!clustering) {
            data.map.addSource('notype', {
                type: 'geojson',
                data: geodataNew,
                cluster: false,
            });

            data.map.addSource('firetype', {
                type: 'geojson',
                data: geodataFire,
                cluster: false,
            });

            data.map.addSource('notypeReopened', {
                type: 'geojson',
                data: geodataReopened,
                cluster: false,
            });

        } else {
            data.map.addSource('notype', {
                type: 'geojson',
                data: geodataNew,
                cluster: true,
                clusterMaxZoom: MAP_CLUSTER_MAX_ZOOM, // Max zoom to cluster points on
                clusterRadius: MAP_CLUSTER_DEFAULT_RADIUS, // Radius of each cluster when clustering points (defaults to 50)
            });

            data.map.addSource('firetype', {
                type: 'geojson',
                data: geodataFire,
                cluster: true,
                clusterMaxZoom: MAP_CLUSTER_MAX_ZOOM, // Max zoom to cluster points on
                clusterRadius: MAP_CLUSTER_DEFAULT_RADIUS, // Radius of each cluster when clustering points (defaults to 50)
            });

            data.map.addSource('notypeReopened', {
                type: 'geojson',
                data: geodataReopened,
                cluster: true,
                clusterMaxZoom: MAP_CLUSTER_MAX_ZOOM, // Max zoom to cluster points on
                clusterRadius: MAP_CLUSTER_DEFAULT_RADIUS, // Radius of each cluster when clustering points (defaults to 50)
            });

            data.map.addLayer({
                //custers notype
                id: 'clusters',
                type: 'circle',
                source: 'notype',
                filter: ['has', 'point_count'],
                paint: {
                    // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
                    // with three steps to implement three types of circles:
                    //   * Blue, 20px circles when point count is less than 100
                    //   * Yellow, 30px circles when point count is between 100 and 750
                    //   * Pink, 40px circles when point count is greater than or equal to 750
                    'circle-color': [
                        'step',
                        ['get', 'point_count'],
                        '#FF7051',
                        100,
                        '#FAA142',
                        750,
                        '#FAD942',
                    ],
                    'circle-radius': [
                        'step',
                        ['get', 'point_count'],
                        20,
                        100,
                        30,
                        750,
                        40,
                    ],
                },
            });

            data.map.addLayer({
                //custers notype
                id: 'clustersReopened',
                type: 'circle',
                source: 'notypeReopened',
                filter: ['has', 'point_count'],
                paint: {
                    // Use step expressions (https://docs.mapbox.com/mapbox-gl-js/style-spec/#expressions-step)
                    // with three steps to implement three types of circles:
                    //   * Blue, 20px circles when point count is less than 100
                    //   * Yellow, 30px circles when point count is between 100 and 750
                    //   * Pink, 40px circles when point count is greater than or equal to 750
                    'circle-color': [
                        'step',
                        ['get', 'point_count'],
                        '#FF7051',
                        100,
                        '#FAA142',
                        750,
                        '#FAD942',
                    ],
                    'circle-radius': [
                        'step',
                        ['get', 'point_count'],
                        20,
                        100,
                        30,
                        750,
                        40,
                    ],
                },
            });

            data.map.addLayer({
                //cluster-count notype
                id: 'cluster-count',
                type: 'symbol',
                source: 'notype',
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': '{point_count_abbreviated}',
                    'text-font': [
                        'DIN Offc Pro Medium',
                        'Arial Unicode MS Bold',
                    ],
                    'text-size': 12,
                },
            });

            data.map.addLayer({
                //cluster-count notype
                id: 'cluster-countReopened',
                type: 'symbol',
                source: 'notypeReopened',
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': '{point_count_abbreviated}',
                    'text-font': [
                        'DIN Offc Pro Medium',
                        'Arial Unicode MS Bold',
                    ],
                    'text-size': 12,
                },
            });
        }
    }

    if (data.currentChoice === 'claims') {
        data.map.addLayer({
            id: 'unclustered-point',
            source: 'notype',
            type: 'symbol',
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-image': 'claim',
                'icon-size': 0.1,
                'icon-anchor': 'bottom',
                'icon-allow-overlap': true,
            },
        });

        data.map.addLayer({
            id: 'unclustered-point-fire',
            source: 'firetype',
            type: 'symbol',
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-image': 'claimFire',
                'icon-size': 0.1,
                'icon-anchor': 'bottom',
                'icon-allow-overlap': true,
            },
        });

        data.map.addLayer({
            id: 'unclustered-pointReopened',
            source: 'notypeReopened',
            type: 'symbol',
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-image': 'claimReopened',
                'icon-size': 0.10,
                'icon-anchor': 'bottom',
                'icon-allow-overlap': true,
            },
        });

        if (typeof data.map.getLayer('bulkData') !== 'undefined') {
            data.map.removeLayer('bulkData').removeSource('bulklist')
        }

        data.map.addSource('bulklist', {
            type: 'geojson',
            data: data.bulkData,
            cluster: false,
        });

        data.map.addLayer({
            id: 'bulkData',
            source: 'bulklist',
            type: 'symbol',
            layout: {
                'icon-image': 'checked',
                'icon-size': 0.6,
                'icon-allow-overlap': true,
            },
        });

        data.map.on('click', 'bulkData', function (e) {
            if (data.popup !== '') {
                data.popup.remove();
            }
            let stackedClaims = []
            let coordinates = e.features[0].geometry.coordinates.slice();

            for (let x in e.features) {
                let stackedClaimName = null
                if (e.features[x].properties.name !== 'null' && e.features[x].properties.name !== null) {
                    stackedClaimName = e.features[x].properties.name;
                } else {
                    stackedClaimName = 'Not Assigned';
                }

                let newClaim = {
                    claimnumber: e.features[x].properties.claimnumber,
                    claimtype: e.features[x].properties.claimtype,
                    clientname: stackedClaimName,
                    id: e.features[x].properties.id,
                    clientid: e.features[x].properties.clientid,
                    long: e.features[x].geometry.coordinates[0],
                    lat: e.features[x].geometry.coordinates[1],
                }

                stackedClaims.push(newClaim);
            }

            coordinates = e.features[0].geometry.coordinates.slice();

            // Ensure that if thedata.map is zoomed out such that
            // multiple copies of the feature are visible, the
            // popup appears over the copy being pointed to.
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }

            let htmlString = ''
            for (let y = 0; y < stackedClaims.length; y++) {
                htmlString += `
            <div style="width: 175px; padding: 5px;">
                <h3>
                    <a href="${localPath}claim/${stackedClaims[y].id}" target="_blank"> Claim: ${stackedClaims[y].claimnumber} </a>
                </h3>
                <h3>
                    <a href="${localPath}client/${stackedClaims[y].clientid}" target="_blank"> Client: ${stackedClaims[y].clientname}</a>
                </h3>
                <div id="map-popup-remove-claim-btn-${y}" style="width: 100%"></div>
            </div>`;
            }

            data.popup = new mapboxgl.Popup()
                .setLngLat(coordinates)
                .setHTML(htmlString)
                .addTo(data.map);

            nextTick(() => {
                for (let y = 0; y < stackedClaims.length; y++) {
                    const element = document.getElementById(`map-popup-remove-claim-btn-${y}`);
                    if (element) {
                        render(
                            h(
                                'div',
                                {
                                    innerHTML: 'Remove Claim',
                                    style: "background-color: white;padding: 2px;border-style: solid; border-color: gray; width: 125px;",
                                    onClick: (e) => {
                                        removeItemFromBulkList(stackedClaims[y].id);
                                    }
                                }
                            ), element);
                    }
                }

            });

        });

    } else {
        data.map.addLayer({
            id: 'fc-unclustered-point',
            source: 'firstCall',
            type: 'symbol',
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-allow-overlap': true,
                'icon-image': 'fc-person',
                'icon-size': 0.6,
            },
        });

        data.map.addLayer({
            id: 'pref-unclustered-point',
            source: 'preferred',
            type: 'symbol',
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-allow-overlap': true,
                'icon-image': 'pref-person',
                'icon-size': 0.6,
            },
        });

        data.map.addLayer({
            id: 'non-pref-unclustered-point',
            source: 'nonPreferred',
            type: 'symbol',
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-allow-overlap': true,
                'icon-image': 'non-pref-person',
                'icon-size': 0.6,
            },
        });

        if (typeof data.map.getLayer('bulkData') !== 'undefined') {
            data.map.removeLayer('bulkData').removeSource('bulklist');
        }

        data.map.addSource('bulklist', {
            type: 'geojson',
            data: data.bulkData,
            cluster: false,
        });
        data.map.addLayer({
            id: 'bulkData',
            source: 'bulklist',
            type: 'symbol',
            layout: {
                'icon-image': 'checked',
                'icon-size': 0.6,
            },
        });

        data.map.on('click', 'bulkData', function (e) {
            if (data.popup !== '') {
                data.popup.remove();
            }
            let stackedClaims = []
            let coordinates = e.features[0].geometry.coordinates.slice();

            for (let x in e.features) {
                let stackedClaimName = null
                if (e.features[x].properties.name !== 'null' && e.features[x].properties.name !== null) {
                    stackedClaimName = e.features[x].properties.name;
                } else {
                    stackedClaimName = 'Not Assigned';
                }

                let newClaim = {
                    claimnumber: e.features[x].properties.claimnumber,
                    claimtype: e.features[x].properties.claimtype,
                    clientname: stackedClaimName,
                    id: e.features[x].properties.id,
                    clientid: e.features[x].properties.clientid,
                    long: e.features[x].geometry.coordinates[0],
                    lat: e.features[x].geometry.coordinates[1],
                }

                stackedClaims.push(newClaim);
            }

            coordinates = e.features[0].geometry.coordinates.slice();

            // Ensure that if thedata.map is zoomed out such that
            // multiple copies of the feature are visible, the
            // popup appears over the copy being pointed to.
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }
            let htmlString = ''
            for (let y = 0; y < stackedClaims.length; y++) {
                htmlString += `
            <div style="width: 175px; padding: 5px;">
                <h3>
                    <a href="${localPath}claim/${stackedClaims[y].id}" target="_blank"> Claim: ${stackedClaims[y].claimnumber}</a>
                </h3>
                <h3>
                    <a href="${localPath}client/${stackedClaims[y].clientid}" target="_blank"> Client: ${stackedClaims[y].clientname}</a>
                </h3>
                <div id="map-popup-remove-claim-from-bulk-list-btn-${y}" style="width: 100%"></div>
            </div>`;
            }

            data.popup = new mapboxgl.Popup()
                .setLngLat(coordinates)
                .setHTML(htmlString)
                .addTo(data.map);

            nextTick(() => {
                for (let y = 0; y < stackedClaims.length; y++) {
                    const element = document.getElementById(`map-popup-remove-claim-from-bulk-list-btn-${y}`);
                    if (element) {
                        render(
                            h(
                                'div',
                                {
                                    innerHTML: 'Remove Claim',
                                    style: "background-color: white;padding: 2px;border-style: solid; border-color: gray; width: 125px;",
                                    onClick: (e) => {
                                        removeItemFromBulkList(stackedClaims[y].id);
                                    }
                                }
                            ), element);
                    }
                }

            });

        });

    }

    // inspect a cluster on click
    data.map.on('click', 'clusters', function (e) {
        let features = data.map.queryRenderedFeatures(e.point, {
            layers: ['clusters'],
        });

        let clusterId = features[0].properties.cluster_id;

        data.map
            .getSource('notype')
            .getClusterExpansionZoom(clusterId, function (err, zoom) {
                if (err) return

                data.map.easeTo({
                    center: features[0].geometry.coordinates,
                    zoom: zoom,
                })
            });
    });

    data.map.on('click', 'clustersReopened', function (e) {
        let features = data.map.queryRenderedFeatures(e.point, {
            layers: ['clustersReopened'],
        });

        let clusterId = features[0].properties.cluster_id;

        data.map
            .getSource('notypeReopened')
            .getClusterExpansionZoom(clusterId, function (err, zoom) {
                if (err) return

                data.map.easeTo({
                    center: features[0].geometry.coordinates,
                    zoom: zoom,
                })
            });
    });

    data.map.on('click', 'prefClusters', function (e) {
        let features = data.map.queryRenderedFeatures(e.point, {
            layers: ['prefClusters'],
        });

        let clusterId = features[0].properties.cluster_id;

        data.map
            .getSource('preferred')
            .getClusterExpansionZoom(clusterId, function (err, zoom) {
                if (err) return

                data.map.easeTo({
                    center: features[0].geometry.coordinates,
                    zoom: zoom,
                })
            });
    });

    data.map.on('click', 'nonPrefClusters', function (e) {
        let features = data.map.queryRenderedFeatures(e.point, {
            layers: ['nonPrefClusters'],
        });

        let clusterId = features[0].properties.cluster_id;

        data.map
            .getSource('nonPreferred')
            .getClusterExpansionZoom(clusterId, function (err, zoom) {
                if (err) return

                data.map.easeTo({
                    center: features[0].geometry.coordinates,
                    zoom: zoom,
                })
            });
    });

    data.map.on('click', 'fcClusters', function (e) {
        let features = data.map.queryRenderedFeatures(e.point, {
            layers: ['fcClusters'],
        });

        let clusterId = features[0].properties.cluster_id;

        data.map
            .getSource('firstCall')
            .getClusterExpansionZoom(clusterId, function (err, zoom) {
                if (err) return

                data.map.easeTo({
                    center: features[0].geometry.coordinates,
                    zoom: zoom,
                })
            });
    });


    const PointTypeClickHandler = (e) => {
        let coordinates = e.features[0].geometry.coordinates.slice();
        let stackedClaims = [];
        if (data.popup !== '') {
            data.popup.remove()
        }
        if (data.currentChoice === 'claims') {

            for (let x in e.features) {
                let stackedClaimName = null;
                if (e.features[x].properties.name !== 'null' && e.features[x].properties.name !== null && e.features[x].properties.name !== undefined) {
                    stackedClaimName = e.features[x].properties.name
                } else {
                    stackedClaimName = 'Not Assigned'
                }
                let newClaim = {
                    claimnumber: e.features[x].properties.claimnumber,
                    claimtype: e.features[x].properties.claimtype,
                    clientname: stackedClaimName,
                    id: e.features[x].properties.id,
                    clientid: e.features[x].properties.clientid,
                    long: e.features[x].geometry.coordinates[0],
                    lat: e.features[x].geometry.coordinates[1],
                    state: e.features[x].properties.state,
                    openedStatus: e.features[x].properties.openedStatus
                }

                stackedClaims.push(newClaim);
            }

        } else {
            let stackedPeople = [];
            for (let x in e.features) {
                let newClaim = {
                    name: e.features[x].properties.name,
                    id: e.features[x].properties.id,
                    phone: e.features[x].properties.mobilephone,
                    status: e.features[x].properties.status,
                    rating: e.features[x].properties.rating,
                    limit: e.features[x].properties.limit,
                    claimcount: e.features[x].properties.claimcount
                }

                if (newClaim.status === 'null') {
                    newClaim.status = 'N/A';
                }

                if (newClaim.limit === 'null') {
                    newClaim.limit = 'Not Set';
                }

                if (newClaim.claimcount === undefined) {
                    newClaim.claimcount = 0;
                }

                stackedPeople.push(newClaim);
            }
        }

        // Ensure that if thedata.map is zoomed out such that
        // multiple copies of the feature are visible, the
        // popup appears over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        if (data.currentChoice === 'claims') {
            let htmlString = '';
            for (let y = 0; y < stackedClaims.length; y++) {
                if (stackedClaims.length > 1) {
                    htmlString += `<div style="border-bottom: 1px solid black;width: 175px; padding: 5px;">`;
                } else {
                    htmlString += `<div style="width: 175px; padding: 5px;">`;
                }
                htmlString += `
            <h3 style="color:gray"> Claim Number: </h3>
            <h2>
                <a style="color:orange;font-weight: 3000;" href="${localPath}claim/${stackedClaims[y].id}" target="_blank">
                    ${stackedClaims[y].claimnumber}
                </a>
            </h2>
            <h3 style="color:gray"> Client Name: </h3>
            <h2 style="padding-bottom:3px;">`;

                if (stackedClaims[y].clientname !== 'Not Assigned') {
                    htmlString +=
                        `<a style="color:orange;font-weight: 3000;" href="${localPath}client/${stackedClaims[y].clientid}" target="_blank">
                ${stackedClaims[y].clientname}
            </a>`;

                } else {
                    htmlString += stackedClaims[y].clientname;
                }

                htmlString += `</h2><div id="map-popup-unclustered-point-btn-${y}" style="width: 100%"></div></div>`;
            }

            data.popup = new mapboxgl.Popup()
                .setLngLat(coordinates)
                .setHTML(htmlString)
                .addTo(data.map);

            nextTick(() => {
                for (let y = 0; y < stackedClaims.length; y++) {
                    const element = document.getElementById(`map-popup-unclustered-point-btn-${y}`);
                    if (element) {
                        render(
                            h(
                                'div',
                                {
                                    innerHTML: 'Add Claim',
                                    style: "background-color: white;padding: 2px;border-style: solid; border-color: gray; width: 125px;",
                                    onClick: (e) => {
                                        addToBulkList(
                                            stackedClaims[y].claimnumber,
                                            stackedClaims[y].claimtype,
                                            stackedClaims[y].clientname,
                                            stackedClaims[y].clientid,
                                            stackedClaims[y].long,
                                            stackedClaims[y].lat,
                                            stackedClaims[y].id,
                                            stackedClaims[y].state,
                                            stackedClaims[y].openedStatus
                                        )
                                    }
                                }
                            ),
                            element);
                    }
                }

            });

        } else {
            let htmlString = '';
            let stackedPeople = [];
            for (let y = 0; y < stackedPeople.length; y++) {
                if (stackedPeople.length > 1) {
                    htmlString += `<div style="border-bottom: 1px solid black;width: 175px; padding: 5px;">`;
                } else {
                    htmlString += `<div style="width: 175px; padding: 5px;">`;
                }
                htmlString += `
            <h1>
                <a style="color:orange;font-weight: 3000;" href="${localPath}userprofile/${stackedPeople[y].id}" target="_blank">
                    ${stackedPeople[y].name}
                </a>
            </h1>
            <h2>Mobile #: ${stackedPeople[y].phone} </h2>
            <h2>Claim Count: ${stackedPeople[y].claimcount} </h2>
            <h2>Claim Limit: ${stackedPeople[y].limit} </h2>
            <h2>Status: ${stackedPeople[y].status} </h2>
            <form>
                <label for="aInfo">Additional Info:</label><br>
                <input class="border: 1px solid #ccc;" type="text" id="aInfo" name="aInfo"><br>
            </form>
            <div id="map-popup-assign-claims-btn-${y}" style="width: 100%"></div>
            <div id="map-popup-set-directions-btn-${y}" style="width: 100%"></div>
          </div>`;
            }

            data.popup = new mapboxgl.Popup()
                .setLngLat(coordinates)
                .setHTML(htmlString)
                .addTo(data.map);

            nextTick(() => {
                for (let y = 0; y < stackedClaims.length; y++) {
                    const element1 = document.getElementById(`map-popup-assign-claims-btn-${y}`);
                    if (element1) {
                        render(
                            h(
                                'div',
                                {
                                    innerHTML: 'Assign Claims',
                                    style: "background-color: white;padding: 2px;border-style: solid; width: 125px;",
                                    onClick: (e) => {
                                        assignClaims(
                                            stackedPeople[y].id,
                                            stackedPeople[y].name
                                        )
                                    }
                                }
                            ),
                            element1);
                    }

                    const element2 = document.getElementById(`map-popup-set-directions-btn-${y}`);
                    if (element2) {
                        render(
                            h(
                                'div',
                                {
                                    innerHTML: 'Add Claim',
                                    style: "background-color: white;padding: 2px;border-style: solid; border-color: gray; width: 125px;",
                                    onClick: (e) => {
                                        setDirections(
                                            coordinates[0],
                                            coordinates[1]
                                        )
                                    }
                                }
                            ),
                            element2);
                    }
                }

            });
        }
    };

    // When a click event occurs on a feature in
    // the unclustered-point layer, open a popup at
    // the location of the feature, with
    // description HTML from its properties.
    data.map.on('click', 'unclustered-point', PointTypeClickHandler);

    data.map.on('click', 'unclustered-point-fire', PointTypeClickHandler);

    data.map.on('click', 'unclustered-pointReopened', function (e) {
        let stackedClaims = []
        if (data.popup !== '') {
            data.popup.remove();
        }

        if (data.currentChoice === 'claims') {
            let coordinates = e.features[0].geometry.coordinates.slice();

            for (let x in e.features) {
                let stackedClaimName = null;
                if (e.features[x].properties.name !== 'null' && e.features[x].properties.name !== null) {
                    stackedClaimName = e.features[x].properties.name
                } else {
                    stackedClaimName = 'Not Assigned'
                }

                let newClaim = {
                    claimnumber: e.features[x].properties.claimnumber,
                    claimtype: e.features[x].properties.claimtype,
                    clientname: stackedClaimName,
                    id: e.features[x].properties.id,
                    clientid: e.features[x].properties.clientid,
                    long: e.features[x].geometry.coordinates[0],
                    lat: e.features[x].geometry.coordinates[1],
                    state: e.features[x].properties.state,
                    openedStatus: e.features[x].properties.openedStatus
                }

                stackedClaims.push(newClaim);
            }

        } else {
            let stackedPeople = [];
            for (let x in e.features) {
                let newClaim = {
                    name: e.features[x].properties.name,
                    id: e.features[x].properties.id,
                    phone: e.features[x].properties.mobilephone,
                    status: e.features[x].properties.status,
                    rating: e.features[x].properties.rating,
                    limit: e.features[x].properties.limit,
                    claimcount: e.features[x].properties.claimcount
                }

                if (newClaim.status === 'null') {
                    newClaim.status = 'N/A';
                }
                if (newClaim.limit === 'null') {
                    newClaim.limit = 'Not Set';
                }
                if (newClaim.claimcount === undefined) {
                    newClaim.claimcount = 0;
                }

                stackedPeople.push(newClaim);
            }

            let coordinates = e.features[0].geometry.coordinates.slice();
        }
        let coordinates = e.features[0].geometry.coordinates.slice();
        // Ensure that if thedata.map is zoomed out such that
        // multiple copies of the feature are visible, the
        // popup appears over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
        }
        if (data.currentChoice === 'claims') {
            let htmlString = ''
            for (let y = 0; y < stackedClaims.length; y++) {
                if (stackedClaims.length > 1) {
                    htmlString += `<div style="border-bottom: 1px solid black;width: 175px; padding: 5px;">`;
                } else {
                    htmlString += `<div style="width: 175px; padding: 5px;">`;
                }
                htmlString += `
            <h3 style="color:gray"> Claim Number: </h3>
            <h2>
                <a style="color:orange;font-weight: 3000;" href="${localPath}claim/${stackedClaims[y].id}" target="_blank">
                    ${stackedClaims[y].claimnumber}
                </a>
            </h2>
            <h3 style="color:gray"> Client Name: </h3>
            <h2 style="padding-bottom:3px;">
                <a style="color:orange;font-weight: 3000;" href="${localPath}client/${stackedClaims[y].clientid}" target="_blank">
                    ${stackedClaims[y].clientname}
                </a>
            </h2>
            <div id="map-popup-add-claim-to-bulk-list-btn-${y}" style="width: 100%"></div>
          </div>`;
            }

            data.popup = new mapboxgl.Popup()
                .setLngLat(coordinates)
                .setHTML(htmlString)
                .addTo(data.map);

            nextTick(() => {
                for (let y = 0; y < stackedClaims.length; y++) {
                    const element = document.getElementById(`map-popup-add-claim-to-bulk-list-btn-${y}`);
                    if (element) {
                        render(
                            h(
                                'div',
                                {
                                    innerHTML: 'Assign Claims',
                                    style: "background-color: white;padding: 2px;border-style: solid; border-color: gray; width: 125px;",
                                    onClick: (e) => {
                                        addToBulkList(
                                            stackedClaims[y].claimnumber,
                                            stackedClaims[y].claimtype,
                                            stackedClaims[y].clientname,
                                            stackedClaims[y].clientid,
                                            stackedClaims[y].long,
                                            stackedClaims[y].lat,
                                            stackedClaims[y].id,
                                            stackedClaims[y].state,
                                            stackedClaims[y].openedStatus,
                                        )
                                    }
                                }
                            ),
                            element);
                    }
                }

            });

        } else {
            let htmlString = '';
            let stackedPeople = [];
            let coordinates = e.features[0].geometry.coordinates.slice();
            for (let y = 0; y < stackedPeople.length; y++) {
                if (stackedPeople.length > 1) {
                    htmlString += `<div style="border-bottom: 1px solid black;width: 175px; padding: 5px;">`
                } else {
                    htmlString += `<div style="width: 175px; padding: 5px;">`
                }
                htmlString += `
            <h1>
                <a style="color:orange;font-weight: 3000;" href="${localPath}userprofile/${stackedPeople[y].id}" target="_blank">
                    ${stackedPeople[y].name}
                </a>
            </h1>
            <h2>Mobile #: ${stackedPeople[y].phone} </h2>
            <h2>Claim Count: ${stackedPeople[y].claimcount} </h2>
            <h2>Claim Limit: ${stackedPeople[y].limit} </h2>
            <h2>Status: ${stackedPeople[y].status} </h2>
            <form>
                <label for="aInfo">Additional Info:</label><br>
                <input class="border: 1px solid #ccc;" type="text" id="aInfo" name="aInfo"><br>
            </form>
            <div id="map-popup-unclustered-point-reopened-assign-claims-btn-${y}" style="width: 100%"></div>
            <div id="map-popup-unclustered-point-reopened-set-directions-btn-${y}" style="width: 100%"></div>
          </div>`;
            }

            data.popup = new mapboxgl.Popup()
                .setLngLat(coordinates)
                .setHTML(htmlString)
                .addTo(data.map);

            nextTick(() => {
                for (let y = 0; y < stackedClaims.length; y++) {
                    const element1 = document.getElementById(`map-popup-assign-claims-btn-${y}`);
                    if (element1) {
                        render(
                            h(
                                'div',
                                {
                                    innerHTML: 'Assign Claims',
                                    style: "background-color: white;padding: 2px;border-style: solid; width: 125px;",
                                    onClick: (e) => {
                                        assignClaims(
                                            stackedPeople[y].id,
                                            stackedPeople[y].name
                                        )
                                    }
                                }
                            ),
                            element1);
                    }

                    const element2 = document.getElementById(`map-popup-set-directions-btn-${y}`);
                    if (element2) {
                        render(
                            h(
                                'div',
                                {
                                    innerHTML: 'Add Claim',
                                    style: "background-color: white;padding: 2px;border-style: solid; border-color: gray; width: 125px;",
                                    onClick: (e) => {
                                        setDirections(
                                            coordinates[0],
                                            coordinates[1]
                                        )
                                    }
                                }
                            ),
                            element2);
                    }
                }

            })

            //data.map.directions.setDestination(coordinates)
        }
    });

    data.map.on('click', 'fc-unclustered-point', function (e) {
        if (data.popup !== '') {
            data.popup.remove();
        }

        let stackedPeople = [];
        for (let x in e.features) {
            let newClaim = {
                name: e.features[x].properties.name,
                id: e.features[x].properties.id,
                phone: e.features[x].properties.mobilephone,
                status: e.features[x].properties.status,
                rating: e.features[x].properties.rating,
                limit: e.features[x].properties.limit,
                claimcount: e.features[x].properties.claimcount
            }

            if (newClaim.status === 'null') {
                newClaim.status = 'N/A';
            }

            if (newClaim.limit === 'null') {
                newClaim.limit = 'Not Set';
            }

            if (newClaim.claimcount === undefined) {
                newClaim.claimcount = 0;
            }

            stackedPeople.push(newClaim);
        }

        let coordinates = e.features[0].geometry.coordinates.slice();

        // Ensure that if thedata.map is zoomed out such that
        // multiple copies of the feature are visible, the
        // popup appears over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        let htmlString = '';

        for (let y = 0; y < stackedPeople.length; y++) {

            if (stackedPeople.length > 1) {
                htmlString += `<div style="border-bottom: 1px solid black;width: 175px; padding: 5px;">`;
            } else {
                htmlString += `<div style="width: 175px; padding: 5px;">`;
            }

            htmlString += `
        <h1>
          <a style="color:orange;font-weight: 3000;" href="${localPath}userprofile/${stackedPeople[y].id}" target="_blank">
            ${stackedPeople[y].name}
          </a>
        </h1>
        <h2> Mobile #: ${stackedPeople[y].phone} </h2>
        <h2> Claim Count: ${stackedPeople[y].claimcount} </h2>
        <h2>Claim Limit: ${stackedPeople[y].limit} </h2>
        <h2>Status: ${stackedPeople[y].status} </h2>
        <form>
            <label for="aInfo">Additional Info:</label><br>
            <input class="border: 1px solid #ccc;" type="text" id="aInfo" name="aInfo"><br>
        </form>
        <div id="map-popup-fc-unclustered-point-assign-claims-btn-${y}" style="width: 100%"></div>
        <div id="map-popup-fc-unclustered-point-set-directions-btn-${y}" style="width: 100%"></div>
      </div>`;
        }

        data.popup = new mapboxgl.Popup()
            .setLngLat(coordinates)
            .setHTML(htmlString)
            .addTo(data.map);

        nextTick(() => {
            for (let y = 0; y < stackedPeople.length; y++) {
                const element1 = document.getElementById(`map-popup-fc-unclustered-point-assign-claims-btn-${y}`);
                if (element1) {
                    render(
                        h(
                            'div',
                            {
                                innerHTML: 'Assign Claims',
                                style: "background-color: white;padding: 2px;border-style: solid; border-color: gray; width: 125px;",
                                onClick: (e) => {
                                    assignClaims(
                                        stackedPeople[y].id,
                                        stackedPeople[y].name,
                                        stackedPeople[y].phone,
                                        stackedPeople[y].claimcount,
                                        stackedPeople[y].limit,
                                        stackedPeople[y].status,
                                    )
                                }
                            }
                        ),
                        element1);
                }

                const element2 = document.getElementById(`map-popup-fc-unclustered-point-set-directions-btn-${y}`);

                if (element2) {
                    render(
                        h(
                            'div',
                            {
                                innerHTML: 'Set Directions',
                                style: "background-color: white;padding: 2px; margin-top: 10px; border-style: solid; border-color: gray; width: 125px;",
                                onClick: (e) => {
                                    setDirections(
                                        coordinates[0],
                                        coordinates[1]
                                    )
                                }
                            }
                        ),
                        element2);
                }
            }

        });
    });

    data.map.on('click', 'pref-unclustered-point', function (e) {
        if (data.popup !== '') {
            data.popup.remove();
        }

        let stackedPeople = [];

        for (let x in e.features) {
            let newClaim = {
                name: e.features[x].properties.name,
                id: e.features[x].properties.id,
                phone: e.features[x].properties.mobilephone,
                status: e.features[x].properties.status,
                rating: e.features[x].properties.rating,
                limit: e.features[x].properties.limit,
                claimcount: e.features[x].properties.claimcount
            }

            if (newClaim.status === 'null') {
                newClaim.status = 'N/A';
            }

            if (newClaim.limit === 'null') {
                newClaim.limit = 'Not Set';
            }

            if (newClaim.claimcount === undefined) {
                newClaim.claimcount = 0;
            }

            stackedPeople.push(newClaim);
        }

        let coordinates = e.features[0].geometry.coordinates.slice();

        // Ensure that if thedata.map is zoomed out such that
        // multiple copies of the feature are visible, the
        // popup appears over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        let htmlString = '';
        for (let y = 0; y < stackedPeople.length; y++) {

            if (stackedPeople.length > 1) {
                htmlString += `<div style="border-bottom: 1px solid black;width: 175px; padding: 5px;">`;
            } else {
                htmlString += `<div style="width: 175px; padding: 5px;">`;
            }
            htmlString += `
        <h1>
          <a style="color:orange;font-weight: 3000;" href="${localPath}userprofile/${stackedPeople[y].id}" target="_blank">
            ${stackedPeople[y].name}
          </a>
        </h1>
        <h2>Mobile #: ${stackedPeople[y].phone} </h2>
        <h2>Claim Count: ${stackedPeople[y].claimcount} </h2>
        <h2>Claim Limit: ${stackedPeople[y].limit} </h2>
        <h2>Status: ${stackedPeople[y].status} </h2>
        <form>
          <label for="aInfo">Additional Info:</label><br>
          <input class="border: 1px solid #ccc;" type="text" id="aInfo" name="aInfo"><br>
        </form>
        <div id="map-popup-pref-unclustered-point-assign-claims-btn-${y}" style="width: 100%"></div>
        <div id="map-popup-pref-unclustered-point-set-directions-btn-${y}" style="width: 100%"></div>
      </div>`;
        }

        data.popup = new mapboxgl.Popup()
            .setLngLat(coordinates)
            .setHTML(htmlString)
            .addTo(data.map);

        nextTick(() => {

            for (let y = 0; y < stackedPeople.length; y++) {

                const element1 = document.getElementById(`map-popup-pref-unclustered-point-assign-claims-btn-${y}`);

                if (element1) {
                    render(
                        h(
                            'div',
                            {
                                innerHTML: 'Assign Claims',
                                style: "background-color: white;padding: 2px;border-style: solid; border-color: gray; width: 125px;",
                                onClick: (e) => {
                                    assignClaims(
                                        stackedPeople[y].id,
                                        stackedPeople[y].name,
                                        stackedPeople[y].phone,
                                        stackedPeople[y].claimcount,
                                        stackedPeople[y].limit,
                                        stackedPeople[y].status,
                                    )
                                }
                            }
                        ),
                        element1);
                }

                const element2 = document.getElementById(`map-popup-pref-unclustered-point-set-directions-btn-${y}`);

                if (element2) {
                    render(
                        h(
                            'div',
                            {
                                innerHTML: 'Set Directions',
                                style: "background-color: white;padding: 2px; margin-top: 10px; border-style: solid; border-color: gray; width: 125px;",
                                onClick: (e) => {
                                    setDirections(
                                        coordinates[0],
                                        coordinates[1]
                                    )
                                }
                            }
                        ),
                        element2);
                }
            }

        });
    });

    data.map.on('click', 'non-pref-unclustered-point', function (e) {
        if (data.popup !== '') {
            data.popup.remove();
        }

        let stackedPeople = []
        for (let x in e.features) {
            let newClaim = {
                name: e.features[x].properties.name,
                id: e.features[x].properties.id,
                phone: e.features[x].properties.mobilephone,
                status: e.features[x].properties.status,
                rating: e.features[x].properties.rating,
                limit: e.features[x].properties.limit,
                claimcount: e.features[x].properties.claimcount
            }

            if (newClaim.status === 'null') {
                newClaim.status = 'N/A'
            }

            if (newClaim.limit === 'null') {
                newClaim.limit = 'Not Set'
            }

            if (newClaim.claimcount === undefined) {
                newClaim.claimcount = 0
            }

            stackedPeople.push(newClaim);
        }

        let coordinates = e.features[0].geometry.coordinates.slice();

        // Ensure that if thedata.map is zoomed out such that
        // multiple copies of the feature are visible, the
        // popup appears over the copy being pointed to.
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        let htmlString = ''
        for (let y = 0; y < stackedPeople.length; y++) {
            if (stackedPeople.length > 1) {
                htmlString += `<div style="border-bottom: 1px solid black;width: 175px; padding: 5px;">`
            } else {
                htmlString += `<div style="width: 175px; padding: 5px;">`
            }
            htmlString += `
        <h1>
          <a style="color:orange;font-weight: 3000;" href="${localPath}userprofile/${stackedPeople[y].id}" target="_blank">
            ${stackedPeople[y].name}
          </a>
        </h1>
        <h2>Mobile #: ${stackedPeople[y].phone} </h2>
        <h2>Claim Count: ${stackedPeople[y].claimcount} </h2>
        <h2>Claim Limit: ${stackedPeople[y].limit} </h2>
        <h2>Status: ${stackedPeople[y].status} </h2>
        <form>
          <label for="aInfo">Additional Info:</label><br>
          <input class="border: 1px solid #ccc;" type="text" id="aInfo" name="aInfo"><br>
        </form>
        <div id="map-popup-non-pref-unclustered-point-assign-claims-btn-${y}" style="width: 100%"></div>
        <div id="map-popup-non-pref-unclustered-point-set-directions-btn-${y}" style="width: 100%"></div>
      </div>`;
        }
        data.popup = new mapboxgl.Popup()
            .setLngLat(coordinates)
            .setHTML(htmlString)
            .addTo(data.map);

        nextTick(() => {
            for (let y = 0; y < stackedPeople.length; y++) {
                const element1 =
                    document.getElementById(`map-popup-non-pref-unclustered-point-assign-claims-btn-${y}`);

                if (element1) {
                    render(
                        h(
                            'div',
                            {
                                innerHTML: 'Assign Claims',
                                style: "background-color: white;padding: 2px;border-style: solid; border-color: gray; width: 125px;",
                                onClick: (e) => {
                                    assignClaims(
                                        stackedPeople[y].id,
                                        stackedPeople[y].name
                                    )
                                }
                            }
                        ),
                        element1);
                }

                const element2 =
                    document.getElementById(`map-popup-non-pref-unclustered-point-set-directions-btn-${y}`);

                if (element2) {
                    render(
                        h(
                            'div',
                            {
                                innerHTML: 'Set Directions',
                                style: "background-color: white;padding: 2px; margin-top: 10px; border-style: solid; border-color: gray; width: 125px;",
                                onClick: (e) => {
                                    setDirections(
                                        coordinates[0],
                                        coordinates[1]
                                    )
                                }
                            }
                        ),
                        element2);
                }
            }

        });
    });

    data.map.on('mouseenter', 'clusters', function () {
        data.map.getCanvas().style.cursor = 'pointer';
    });
    data.map.on('mouseleave', 'clusters', function () {
        data.map.getCanvas().style.cursor = '';
    });

    data.map.on('mouseenter', 'clustersReopened', function () {
        data.map.getCanvas().style.cursor = 'pointer';
    });
    data.map.on('mouseleave', 'clustersReopened', function () {
        data.map.getCanvas().style.cursor = '';
    });

    data.loading = false;
}

const updatePeopleSelectionList = (personId) => {
    const i = data.personSelection.indexOf(personId);
    if (i !== -1) {
        data.personSelection.splice(i, 1);
    } else {
        data.personSelection.push(personId);
    }
}

const GetFormattedSendToList = () => {
    if (!textMessageSendToList)
        return 'N/A';

    let personList = '';
    for (const [i, person] of textMessageSendToList.entries()) {
        let str = i !== textMessageSendToList.length - 1 ? ', ' : '';
        personList += person.name + str;
    }
    return personList;
}

const InitialContactTemplate = `This message is from AAN.
Are you available to work the following claim(s)?
[CLAIMDETAILS]`;

const PerClaimTemplateDetails = `[CLIENT] - Type: [TYPE]\n`;    //XXXX add minified url to claim?

const ParseMessageTemplate = (template, contentMap) => {
    let tpl = template;
    contentMap.forEach((val, key) => {
        tpl = tpl.replace('[' + key.toUpperCase() + ']', val);
    });
    return tpl;
}

//Selected people who can be texted (selection survives filter changes, so check again here)
const GetSelectedRecipients = () => {
    return data.sortedPeople.filter((p) => data.personSelection.includes(p.id) && p.texting_enabled === true);
}

const SelectRecipientsHandler = () => {
    let emptyClaims = data.bulkList.length === 0;

    textMessageSendToList = [];
    textMessageRecipients = [];

    let claims = [];
    let parsedClaimData = '';
    const recipients = GetSelectedRecipients();

    if (recipients.length === 0) {
        ToastUtil.warningMsg('No adjusters selected', 3500);
        return;
    } else if (emptyClaims) {
        ToastUtil.warningMsg('No Claims selected', 3500);
        return;
    }

    for (const p of recipients) {
        textMessageRecipients.push(p.id);  //for api
        textMessageSendToList.push({name: p.name, phone: p.phone});
        textMessageFormattedRecipients = GetFormattedSendToList();
    }

    for (const claim of data.bulkList) {
        claims.push(claim.claimid);
        let claimData = new Map();
        claimData.set("client", claim.clientname);
        claimData.set("type", claim.claimtype);
        parsedClaimData += ParseMessageTemplate(PerClaimTemplateDetails, claimData);
    }

    textMessageClaims = claims;

    let contents = new Map();
    contents.set("claimdetails", parsedClaimData);
    //Note: ideally, the template and parsing would be an API call, because a LOT more data would be accessible
    textMessageContents.value = ParseMessageTemplate(InitialContactTemplate, contents);

    data.messageDialog = true;
}

//Longer than one SMS is fine (it goes as a single MMS), but keep texts short. The API adds the STOP footer after this.
const TEXT_MESSAGE_MAX_LENGTH = 500;

const SendTextMessageHandler = () => {
    if (!textMessageContents.value || textMessageContents.value.length === 0) {
        ToastUtil.errorMsg('Error. Text message contents is empty', 5500);
        return;
    } else if (textMessageContents.value.length > TEXT_MESSAGE_MAX_LENGTH) {
        ToastUtil.errorMsg(`Error. Text message is longer than ${TEXT_MESSAGE_MAX_LENGTH} characters`, 5500);
        return;
    } else if (!textMessageRecipients || textMessageRecipients.length === 0) {
        ToastUtil.errorMsg('Error. Text message recipients is empty', 5500);
        return;
    }

    ApiWrapper.post('conversations', 'new-assignments', {
        message: textMessageContents.value,
        recipients: textMessageRecipients,
        claims: textMessageClaims
    })
        .then((res) => {
            //TODO Force "re-draw" to update what was in bulk list?
            console.dir(res);
            ToastUtil.successMsg(`${res.toast}`, 3500);
            //Reset immediately for cleaner state
            textMessageContents.value = '';
            textMessageSendToList = [];
            textMessageClaims = [];
            textMessageRecipients = [];
        })
        .catch((err) => {
            console.error(err);
            let apiErrMsg = (err instanceof AxiosError) ? err.response.data.message : err.message;
            ToastUtil.errorMsg(`[${err.code}] ${apiErrMsg}`, 5000);
        })
        .finally(() => {
            data.sendingMessage = false;
            data.messageDialog = false
        });
}

</script>

<style scoped>
.mapboxgl-map {
    width: 100%;
    height: 750px;
}

.mapbox-directions-step {
    position: relative;
    color: rgba(255, 255, 255, .75);
    cursor: pointer;
    padding: 20px 20px 20px 40px;
    font-size: 20px !important;
    font-weight: bold !important;
    line-height: 18px;
    font-weight: 300;
    letter-spacing: 0.1em;
}

.mapbox-directions-route-summary {
    position: relative;
    z-index: 1;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.75);
    color: #fff;
    padding: 5px 10px;
    font-size: 20px !important;
    font-weight: bold !important;
    line-height: 28px;
}

#map {
    top: 0;
    bottom: 0;
}

.marker {
    background-image: url('/images/mapbox-A.png');
    background-size: cover;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    cursor: pointer;
}

.mapboxgl-popup-content {
    font-family: 'Oswald';
    box-shadow: 0 3px 14px rgba(0, 0, 0, 0.4);
    max-width: 200px;
}

.mapboxgl-popup-close-button {
    padding-right: 5px;
    border-radius: 1px;
    font-size: 32px;
}

.mapboxgl-popup-content-wrapper {
    border-radius: 12px;
}

.adjuster-hover {
    background-color: rgba(var(--v-theme-primary), 0.75);
    color: #fff;
    cursor: pointer;
}
</style>

<template>
    <v-container fluid>
        <v-card class="elevation-2">
            <v-toolbar elevation="2" color="white" class="pa-0">
                <v-toolbar-title class="pa-0 pl-5 ma-0 text-left">
                    <span class="font-weight-bold text-lg-h6"> Bulk Assign </span>
                </v-toolbar-title>

                <v-progress-linear
                    :active="data.loading"
                    :indeterminate="data.loading"
                    absolute
                    bottom
                    color="primary"
                >
                </v-progress-linear>

                <v-text-field
                    class="px-3 mt-5"
                    label="Search Map"
                    single-line
                    clearable
                    @change="searchMap"
                    @click:clear="searchMap"
                    v-model="data.searchRequest"
                    append-inner-icon="mdi-magnify"
                >
                </v-text-field>
                <v-text-field
                    class="px-3 mt-5"
                    v-if="data.currentChoice === 'people'"
                    v-model="data.searchAdjuster"
                    append-inner-icon="mdi-magnify"
                    label="Search An Adjuster"
                    single-line
                    @change="selectPerson()"
                ></v-text-field>

                <v-spacer></v-spacer>

                <span class="ma-0 pr-4">
          <v-btn
              v-if="data.currentChoice === 'people'"
              color="primary"
              class="ml-2 mr-1"
              size="small"
              variant="elevated"
              dark
              @click.stop="data.drawer = !data.drawer"
          >
            Filters
          </v-btn>
                    <!-- BG-NOTE (10/08/26) I cannot see the use of toggling Street vs Satellite, hiding
                    <v-btn
                        v-if="data.streetView"
                        @click="init('satellite-v9'); data.streetView=false;"
                        class="ml-2 mr-1"
                        variant="elevated"
                        color="primary"
                        size="small"
                        dark
                    >
                      Street View
                    </v-btn>
                    <v-btn
                        v-else
                        @click="init('streets-v11');data.streetView=true;"
                        class="ml-2 mr-1"
                        variant="elevated"
                        color="primary"
                        size="small"
                        dark
                    >
                      Satellite View
                    </v-btn>
                    -->

          <v-btn
              v-if="!data.showingAppointments"
              @click="data.map.setLayoutProperty('appLayer', 'visibility', 'visible'); data.showingAppointments=true"
              class="ml-2 mr-1"
              variant="elevated"
              color="primary"
              size="small"
              dark
          >
            Appointments
          </v-btn>
          <v-btn
              v-else
              @click="data.map.setLayoutProperty('appLayer', 'visibility', 'none'); data.showingAppointments=false"
              class="ml-2 mr-1"
              variant="elevated"
              color="error"
              size="small"
              dark
          >
            No Appointments
          </v-btn>
          <v-btn
              v-if="!data.clustering"
              @click="data.clustering=true; getData()"
              class="ml-2 mr-1"
              variant="elevated"
              color="primary"
              size="small"
              dark
          >
            Cluster
          </v-btn>
          <v-btn
              v-else
              @click="data.clustering=false; getData()"
              class="ml-2 mr-1"
              variant="elevated"
              color="error"
              size="small"
              dark
          >
            Stop Clustering
          </v-btn>
          <v-btn
              v-if="data.showingDirections"
              @click="resetDirections()"
              class="ml-2"
              variant="elevated"
              color="primary"
              size="small"
              dark
          >
            Remove Directions
          </v-btn>
        </span>
            </v-toolbar>

            <v-navigation-drawer
                v-model="data.drawer"
                v-if="data.currentChoice === 'people'"
                :width="325"
                temporary
            >
                <v-list dense>
                    <v-list-item>
                        <v-checkbox v-model="data.firstCallModel" label="First Call"></v-checkbox>
                    </v-list-item>
                    <v-list-item>
                        <v-checkbox v-model="data.preferredModel" label="Preferred"></v-checkbox>
                    </v-list-item>
                    <v-list-item>
                        <v-checkbox v-model="data.statusModel" label="Active"></v-checkbox>
                    </v-list-item>
                    <v-list-item>

                        <v-autocomplete
                            class="px-3"
                            v-model="data.certificationModel"
                            :items="data.certificationItems"
                            label="Select Certification(s)"
                            multiple
                            clearable
                        ></v-autocomplete>

                    </v-list-item>
                    <v-list-item>

                        <v-autocomplete
                            class="px-3"
                            v-model="data.stateModel"
                            :items="data.stateItems"
                            label="Select State License(s)"
                            multiple
                            clearable
                        ></v-autocomplete>

                    </v-list-item>
                    <v-list-item>
                        <!--
                        BG-NOTE (10/08/26) - Unused / Incomplete -- hiding for now
                        <v-menu offset-y @click.stop>
                          <template v-slot:activator="{ on, props }">
                            <v-btn color="primary" dark v-bind="props" v-on="on">
                              Select Ratings
                            </v-btn>
                          </template>
                          <v-list>
                            <v-list-item v-for="(item, index) in data.ratingItems" :key="index">
                              <v-list-item-title>{{ item.category }}</v-list-item-title>
                              <v-rating
                                  v-model="item.value"
                                  background-color="warning"
                                  color="warning"
                                  dense
                                  hover
                                  size="20"
                              ></v-rating>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                        -->
                    </v-list-item>

                    <v-list-item>

                        <v-btn
                            color="primary"
                            variant="elevated"
                            dark
                            @click="selectPerson()"
                            class="px-3"
                        >
                            Apply Filters
                        </v-btn>

                    </v-list-item>
                </v-list>

            </v-navigation-drawer>

            <v-row class="mt-7">
                <v-col cols="4" class="text-left">
                    <span class="pl-5">New Claim: </span>
                    <v-icon color="blue">mdi-map-marker-question</v-icon>
                    <span class="pl-5">Reopened Claim: </span>
                    <v-icon color="red">mdi-map-marker-question</v-icon>
                    <span class="pl-5">Fire Claim: </span>
                    <v-icon color="orange">mdi-fire-circle</v-icon>
                </v-col>
                <v-col cols="4" class="text-center">
                    <v-btn
                        v-if="data.currentChoice === 'claims'"
                        variant="elevated"
                        color="default"
                        @click="selectPerson()"
                        class=""
                    >
                        Select A Person
                    </v-btn>
                    <v-btn
                        v-if="data.currentChoice === 'people'"
                        variant="elevated"
                        color="default"
                        @click="ToggleMapToClaims"
                    >
                        Return To Claims
                    </v-btn>
                    <!-- FIXME Above will always error if anything is in bulk list -->
                </v-col>
                <v-col cols="4" class="text-right">
                    <v-text-field
                        type="number"
                        v-model="data.claimAge"
                        label="Minimum Claim Age (Days)"
                        clearable
                        @change="createMapMarkers(data.geodata, 'claim', data.clustering)"
                    ></v-text-field>
                </v-col>
            </v-row>

            <v-row class="text-center">
                <v-col class="text-center" cols="8" :sm="data.mapCols">
                    <div id="map" style="width:100%; height:750px"></div>
                </v-col>
                <v-col v-if="data.currentChoice === 'people'" cols="4">
                    <v-row dense no-gutters>
                        <!-- Map-Actions Mode select -->
                        <v-col cols="12" class="text-center ma-0 mb-2">
                            <v-btn-group>
                                <v-btn
                                    @click="AdjustersListingModeSwitch(false)"
                                    no-ripple
                                    v-bind:variant="data.sendingMessage === false ? 'flat' : 'tonal'"
                                    v-bind:color="data.sendingMessage === false ? 'primary' : 'default'"
                                    class="text-sm-body-1 pa-2 ma-0"
                                >
                                    Assign Claim(s)
                                </v-btn>
                                <v-btn
                                    @click="AdjustersListingModeSwitch(true)"
                                    no-ripple
                                    v-bind:variant="data.sendingMessage === true ? 'flat' : 'tonal'"
                                    v-bind:color="data.sendingMessage === true ? 'primary' : 'default'"
                                    class="text-sm-body-1 pa-2 ma-0"
                                >
                                    Send Text(s)
                                </v-btn>
                            </v-btn-group>
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col cols="12" class="text-left pa-0">
                            <span class="font-weight-bold text-lg-h6">Adjusters:</span>
                        </v-col>
                    </v-row>
                    <v-row no-gutters class="mt-2 align-center ga-2 flex-nowrap">
                        <v-col>
                            <v-text-field
                                v-model="adjusterFilters.search"
                                label="Search adjusters by name"
                                prepend-inner-icon="mdi-magnify"
                                variant="outlined"
                                density="compact"
                                hide-details
                                clearable
                            ></v-text-field>
                        </v-col>
                        <v-col cols="auto">
                            <v-select
                                v-model="adjusterFilters.sortBy"
                                :items="ADJUSTER_SORT_OPTIONS"
                                label="Sort"
                                variant="outlined"
                                density="compact"
                                hide-details
                                style="width: 170px;"
                            ></v-select>
                        </v-col>
                    </v-row>
                    <v-row no-gutters class="mt-1 align-center">
                        <v-col class="d-flex flex-wrap align-center">
                            <v-checkbox v-model="adjusterFilters.preferred" label="Preferred" density="compact" hide-details class="flex-grow-0 mr-4"></v-checkbox>
                            <v-checkbox v-model="adjusterFilters.firstCall" label="First Call" density="compact" hide-details class="flex-grow-0 mr-4"></v-checkbox>
                            <v-checkbox
                                :model-value="data.sendingMessage || adjusterFilters.textingOnly"
                                @update:model-value="(val) => adjusterFilters.textingOnly = val"
                                :disabled="data.sendingMessage"
                                label="Texting on"
                                density="compact" hide-details class="flex-grow-0"
                            ></v-checkbox>
                        </v-col>
                        <v-col cols="auto">
                            <span class="text-caption text-medium-emphasis text-no-wrap">{{ filteredAdjusters.length }} adjusters</span>
                        </v-col>
                    </v-row>
                    <v-card height="auto" elevation="2" class="w-100 pa-1 ma-0 mt-2">
                        <v-data-iterator
                            :items="filteredAdjusters"
                            :items-per-page="adjusterFilters.perPage"
                            v-model:page="adjusterFilters.pageNum"
                            item-value="id"
                            class="ma-1 text-left"
                        >
                            <template v-slot:default="{ items }">
                                <template v-for="item in items" :key="item.raw.id">
                                    <v-hover>
                                        <template v-slot:default="{ isHovering, props }">
                                            <v-row
                                                v-bind="props"
                                                :class="'ma-0 pa-0 py-1 align-start ' + (isHovering ? 'adjuster-hover' : '')"
                                                @click="data.sendingMessage ? updatePeopleSelectionList(item.raw.id) : viewAdjuster(item.raw)"
                                            >
                                                <v-col cols="auto" v-if="data.sendingMessage === true" class="ma-0 pa-0 pr-1">
                                                    <v-checkbox-btn
                                                        class="ma-0 pa-0"
                                                        :model-value="data.personSelection.includes(item.raw.id)"
                                                        @click.stop="updatePeopleSelectionList(item.raw.id)"
                                                    ></v-checkbox-btn>
                                                </v-col>
                                                <v-col class="ma-0 pa-0">
                                                    <div class="d-flex align-center ga-2">
                                                        <span class="text-body-1 text-truncate">{{ item.raw.name }}</span>
                                                        <v-chip v-if="IsTrue(item.raw.preferred)" size="x-small" color="primary" variant="tonal" label>Preferred</v-chip>
                                                        <v-chip v-if="IsTrue(item.raw.firstcall)" size="x-small" color="secondary" variant="tonal" label>First Call</v-chip>
                                                        <v-spacer></v-spacer>
                                                        <span class="text-body-2 text-medium-emphasis text-no-wrap">{{ FormatAdjusterLocDist(item.raw.distance) }}</span>
                                                        <span
                                                            class="text-body-2 text-no-wrap adjuster-limit"
                                                            :class="IsAtClaimLimit(item.raw) ? 'text-error font-weight-bold' : 'text-medium-emphasis'"
                                                            title="Claims / limit"
                                                        >{{ item.raw.claimcount ?? 0 }} / {{ item.raw.limit ?? '-' }}</span>
                                                        <v-icon
                                                            size="small"
                                                            :color="item.raw.texting_enabled === true ? 'blue' : 'grey-lighten-1'"
                                                            :title="item.raw.texting_enabled === true ? 'Can be texted' : 'Texting off'"
                                                        >{{ item.raw.texting_enabled === true ? 'mdi-cellphone-message' : 'mdi-cellphone-message-off' }}
                                                        </v-icon>
                                                    </div>
                                                    <div v-if="item.raw.phone" class="text-caption text-medium-emphasis">{{ formatPhoneNumber(item.raw.phone) }}</div>
                                                    <div v-if="item.raw.shortcomment" class="text-caption">{{ item.raw.shortcomment }}</div>
                                                </v-col>
                                            </v-row>
                                        </template>
                                    </v-hover>
                                </template>
                            </template>
                            <template v-slot:no-data>
                                <div class="pa-4 text-center text-medium-emphasis">No adjusters match these filters</div>
                            </template>
                            <template v-slot:footer="{ pageCount }">
                                <v-divider thickness="1" class="ma-2"></v-divider>
                                <v-pagination
                                    v-model="adjusterFilters.pageNum"
                                    :length="pageCount"
                                    density="compact"
                                    rounded
                                    total-visible="7"
                                ></v-pagination>
                            </template>
                        </v-data-iterator>
                    </v-card>
                    <div v-if="data.sendingMessage && data.personSelection.length > 0" class="text-caption text-center mt-2">
                        {{ data.personSelection.length }} selected
                        <a class="ml-2 text-primary" style="cursor: pointer;" @click="ClearPersonSelectionLists">Clear</a>
                    </div>
                    <v-row v-if="data.sendingMessage" dense no-gutters>
                        <v-col cols="12" class="text-center ma-0 mt-5">
                            <v-btn
                                color="primary"
                                variant="elevated"
                                elevation="4"
                                @click="SelectRecipientsHandler"
                                class="px-3"
                            >
                                Select Recipients(s)
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>

            <v-dialog
                v-model="data.messageDialog"
                max-width="700"
                class="w-lg-auto"
            >
                <v-card class="pa-2 ma-2 border-thin" variant="elevated" elevation="4">
                    <v-card-title class="ma-2 mt-0 pa-0 text-left"> Send Text Message</v-card-title>
                    <v-card-subtitle class="text-lg-subtitle-1 bold text-black pa-0 ma-0 mb-1 text-wrap">
                        Recipient(s): {{ textMessageFormattedRecipients }}
                    </v-card-subtitle>
                    <v-textarea
                        rows="4"
                        variant="outlined"
                        :counter="TEXT_MESSAGE_MAX_LENGTH"
                        :rules="[v => (v || '' ).length <= TEXT_MESSAGE_MAX_LENGTH || `Max ${TEXT_MESSAGE_MAX_LENGTH} characters`]"
                        v-model="textMessageContents"
                        class="pa-0 ma-0"
                    ></v-textarea>

                    <v-divider class="ma-2 mb-3"></v-divider>

                    <v-card-actions class="ma-0 pa-0">
                        <v-btn color="error" variant="flat" class="float-left d-inline ml-1" @click="data.messageDialog = false"> Cancel</v-btn>
                        <v-spacer></v-spacer>
                        <v-btn color="primary" variant="flat" @click="SendTextMessageHandler"> Send Text(s)</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-row>
                <v-col cols="12">
                    <v-divider class="ma-12 pa-0 text-center" thickness="2">
                        <h2>Claims To Bulk Assign</h2>
                    </v-divider>
                    <v-data-table
                        :headers="data.bulkHeaders"
                        :items="data.bulkList"
                        class="elevation-1 ma-0"
                        :items-per-page-options="[5, 10, 15, 30, 50, 100]"
                    >
                        <template v-slot:[`item.claimnumber`]="{ item }">
                            <RouterLink :to="`/claim/${item.claimid}`" target="_blank">
                                {{ item.claimnumber }}
                            </RouterLink>
                        </template>
                        <template v-slot:[`item.clientname`]="{ item }">
                            {{ item.clientname }}
                        </template>
                        <template v-slot:[`item.actions`]="{ item }">
                            <v-btn
                                size="x-small"
                                variant="elevated"
                                color="error"
                                @click="removeFromBulkList(item)"
                            >
                                <v-icon> mdi-close</v-icon>
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-card>
        <div class="px-5">
            <p>
                Icons made by <a href="https://www.flaticon.com/authors/good-ware" title="Good Ware">Good Ware</a>
                from <a href="https://www.flaticon.com/" title="Flaticon">www.flaticon.com</a>
            </p>
        </div>
        <v-dialog
            v-model="data.showMultipleAdjusterConfirmation"
            width="auto"
            max-width="750"
        >
            <v-card title="Claims Have Adjuster Assigned">
                <v-card-text>
                    <v-alert color="warning" text="The following claims already have an adjuster assigned:"></v-alert>
                    <v-data-table :items="data.claimsWithAdjusters" hide-default-footer>
                    </v-data-table>
                    Do you still want to assign these claims to {{ data.currentSelectedAdjuster }}?
                </v-card-text>
                <v-card-actions>
                    <v-btn-group>
                        <v-btn color="error" @click="data.showMultipleAdjusterConfirmation=false">
                            Close
                        </v-btn>
                        <v-btn color="success" @click="data.showMultipleAdjusterConfirmation=false; assignBulkClaimsToAdjuster(data.currentSelectedAdjusterId)">
                            Assign
                        </v-btn>
                    </v-btn-group>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>